
// Build: 2026-05-20-storydashboard-illustrated-manuscript-format-v1
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { useUser } from "../UserContext";
import { api } from "../api";
import { TEXT } from "../i18n";
import { getPublicationInfo } from "../utils/publicationStatus";
import ProjectBreadcrumbTrail from "../components/ProjectBreadcrumbTrail";
import StoryDashboardOwnerSettings from "../components/StoryDashboardOwnerSettings";
import {
  sharedProjectContextPilotEnabled,
} from "../utils/sharedProjectContextPilot";

const DASHBOARD_LIST_LIMIT = 200;
const STORY_CACHE_LIMIT = 80;
const STORY_LIST_PAGE_SIZE = 6;
const CANON_PAGE_SIZE = 20;
const SCENE_REACTION_CODES = Object.freeze([
  "want_more",
  "exciting",
  "emotional",
  "like_character",
  "confusing",
  "too_long",
  "afraid_for_character",
  "curious_ending",
]);

const NEXTSCENES_POLICY_LINKS = {
  en: [
    { label: "Writer Rights", href: "https://www.nextscenes.org/writer-rights" },
    { label: "Contributor Policy", href: "https://www.nextscenes.org/contributor-policy" },
    { label: "Publication and Benefit-Sharing", href: "https://www.nextscenes.org/publication-benefit-sharing" },
    { label: "Plain-Language Terms", href: "https://www.nextscenes.org/plain-language-terms" },
  ],
  fr: [
    { label: "Droits des auteurs", href: "https://www.nextscenes.org/fr/droits-des-auteurs" },
    { label: "Politique des contributeurs", href: "https://www.nextscenes.org/fr/politique-contributeurs" },
    { label: "Publication et partage des bénéfices", href: "https://www.nextscenes.org/fr/publication-partage-benefices" },
    { label: "Conditions simples", href: "https://www.nextscenes.org/fr/conditions-simples" },
  ],
};

function getNextScenesPolicyLinks(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_POLICY_LINKS.fr
    : NEXTSCENES_POLICY_LINKS.en;
}

function PolicyLinksCard({ lang, title, body, compact = false }) {
  const links = getNextScenesPolicyLinks(lang);

  return (
    <div className={compact ? "ns-policy-links-card ns-policy-links-card-compact" : "ns-policy-links-card"} role="note">
      <div className="ns-policy-links-title">{title}</div>
      {body ? <p className="ns-policy-links-body">{body}</p> : null}
      <div className="ns-policy-links-row">
        {links.map((item) => (
          <a
            key={item.href}
            className="ns-policy-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
}


function safeText(v) {
  return String(v || "").trim();
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function sharedContextLabel(value, type, isFR) {
  const key = safeText(value).toLowerCase();

  const labels = {
    projectType: {
      story: {
        en: "Story",
        fr: "Histoire",
      },
      textbook: {
        en: "Textbook",
        fr: "Manuel scolaire",
      },
    },

    visibility: {
      private_draft: {
        en: "Private Draft",
        fr: "Brouillon privé",
      },
      nextscenes_readers: {
        en: "NextScenes Readers",
        fr: "Lecteurs NextScenes",
      },
      public_preview: {
        en: "Public Preview",
        fr: "Aperçu public",
      },
      published: {
        en: "Published",
        fr: "Publié",
      },
    },

    workflow: {
      canon_writing: {
        en: "Canon Writing",
        fr: "Écriture du canon",
      },
      proposal_round_open: {
        en: "Proposal Round Open",
        fr: "Cycle de propositions ouvert",
      },
      proposal_review: {
        en: "Proposal Review",
        fr: "Examen des propositions",
      },
      completed: {
        en: "Completed",
        fr: "Terminé",
      },
      published: {
        en: "Published",
        fr: "Publié",
      },
      archived: {
        en: "Archived",
        fr: "Archivé",
      },
    },

    transition: {
      add_canon: {
        en: "Add Canon Scene",
        fr: "Ajouter une scène canon",
      },
      open_proposal_round: {
        en: "Open Proposal Round",
        fr: "Ouvrir un cycle de propositions",
      },
      close_proposal_round: {
        en: "Close Proposal Round",
        fr: "Fermer le cycle de propositions",
      },
      submit_proposal: {
        en: "Submit Proposal",
        fr: "Soumettre une proposition",
      },
      approve_proposal: {
        en: "Approve Proposal",
        fr: "Approuver une proposition",
      },
      reject_proposal: {
        en: "Reject Proposal",
        fr: "Rejeter une proposition",
      },
      reject_all_proposals: {
        en: "Reject All Proposals",
        fr: "Rejeter toutes les propositions",
      },
      remove_canon: {
        en: "Remove Canon Scene",
        fr: "Retirer une scène canon",
      },
      restore_canon: {
        en: "Restore Canon Scene",
        fr: "Restaurer une scène canon",
      },
      mark_completed: {
        en: "Mark Completed",
        fr: "Marquer comme terminé",
      },
      change_visibility: {
        en: "Change Visibility",
        fr: "Modifier la visibilité",
      },
      update_publication: {
        en: "Update Publication",
        fr: "Mettre à jour la publication",
      },
    },
  };

  const item = labels?.[type]?.[key];

  if (item) {
    return isFR ? item.fr : item.en;
  }

  return safeText(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getId(v) {
  return v?._id || v?.id || null;
}

function getIdAliases(v) {
  const out = [];
  const a = safeText(v?._id);
  const b = safeText(v?.id);
  if (a) out.push(a);
  if (b && b !== a) out.push(b);
  return out;
}

function normalizeStoriesListResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.stories)) return payload.stories;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function buildStoryCacheKey(storyLike) {
  return safeText(storyLike?._id || storyLike?.id);
}

function clampWords(text, maxWords) {
  const t = safeText(text);
  if (!t) return "";
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length <= maxWords) return t;
  return parts.slice(0, maxWords).join(" ") + "…";
}

function formatDateTime(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");
  } catch {
    return d.toLocaleString();
  }
}

function formatRelativeTime(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const diffSeconds = Math.floor((Date.now() - d.getTime()) / 1000);
  const isFR = lang === "fr";

  if (diffSeconds < 60) return isFR ? "à l’instant" : "just now";

  if (diffSeconds < 3600) {
    const mins = Math.floor(diffSeconds / 60);
    if (isFR) return mins === 1 ? "il y a 1 min" : `il y a ${mins} min`;
    return mins === 1 ? "1 min ago" : `${mins} mins ago`;
  }

  if (diffSeconds < 86400) {
    const hrs = Math.floor(diffSeconds / 3600);
    if (isFR) return hrs === 1 ? "il y a 1 h" : `il y a ${hrs} h`;
    return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
  }

  const days = Math.floor(diffSeconds / 86400);
  if (days < 30) {
    if (isFR) return days === 1 ? "il y a 1 jour" : `il y a ${days} jours`;
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    if (isFR) return months === 1 ? "il y a 1 mois" : `il y a ${months} mois`;
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  if (isFR) return years === 1 ? "il y a 1 an" : `il y a ${years} ans`;
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

function normalizeMode(mode) {
  const s = safeText(mode).toLowerCase();
  if (!s) return "collaborative";
  if (s.includes("solo")) return "solo";
  if (s.includes("group") && s.includes("closed")) return "group_closed";
  if (s.includes("group") && s.includes("open")) return "group_open";
  if (s.includes("collab")) return "collaborative";
  return "collaborative";
}

function normalizeProposalState(story) {
  if (story?.proposalMode === true) return "proposal";
  const s = safeText(
    story?.proposalState ||
      story?.canonState ||
      story?.storyState ||
      story?.collaborationState ||
      story?.state
  ).toLowerCase();

  if (s.includes("proposal")) return "proposal";
  return "canon";
}

function modeMeta(mode, ui) {
  const key = normalizeMode(mode);
  const base = {
    label: ui.collaborativeWriting,
    bg: "rgba(226, 248, 242, 0.92)",
    border: "rgba(16,185,129,0.42)",
    text: "rgba(6,95,70,0.95)",
    dot: "rgba(16,185,129,0.95)",
  };

  if (key === "solo") {
    return {
      ...base,
      label: ui.soloWriting,
      bg: "rgba(246, 242, 234, 0.94)",
      border: "rgba(120,113,108,0.38)",
      text: "rgba(68,64,60,0.95)",
      dot: "rgba(87,83,78,0.95)",
    };
  }

  if (key === "group_open") {
    return {
      ...base,
      label: ui.groupWritingOpen,
      bg: "rgba(245, 244, 255, 0.92)",
      border: "rgba(99,102,241,0.32)",
      text: "rgba(49,46,129,0.95)",
      dot: "rgba(99,102,241,0.90)",
    };
  }

  if (key === "group_closed") {
    return {
      ...base,
      label: ui.groupWritingClosed,
      bg: "rgba(255,247,237,0.94)",
      border: "rgba(245,158,11,0.34)",
      text: "rgba(124,45,18,0.95)",
      dot: "rgba(245,158,11,0.90)",
    };
  }

  return base;
}

function sceneStatusPillMeta(status, ui) {
  const s = safeText(status).toLowerCase();
  if (s === "approved" || s === "canon") {
    return {
      label: ui.canon,
      bg: "rgba(226, 248, 242, 0.92)",
      border: "rgba(16,185,129,0.42)",
      text: "rgba(6,95,70,0.95)",
    };
  }
  if (s === "pending") {
    return {
      label: ui.pending,
      bg: "rgba(255,247,237,0.94)",
      border: "rgba(245,158,11,0.34)",
      text: "rgba(124,45,18,0.95)",
    };
  }
  if (s === "rejected") {
    return {
      label: ui.rejected,
      bg: "rgba(254,242,242,0.94)",
      border: "rgba(239,68,68,0.34)",
      text: "rgba(153,27,27,0.95)",
    };
  }
  return {
    label: safeText(status) || ui.unknown,
    bg: "rgba(241,245,249,0.94)",
    border: "rgba(100,116,139,0.22)",
    text: "rgba(51,65,85,0.95)",
  };
}

function safeSceneText(scene) {
  return safeText(scene?.text || scene?.body || scene?.content || scene?.sceneText || "");
}

function safeSceneIllustration(scene) {
  const url = safeText(scene?.illustrationUrl);
  if (!url) return null;
  return {
    url,
    caption: safeText(scene?.illustrationCaption),
    credit: safeText(scene?.illustrationCredit),
    alt: safeText(scene?.illustrationAltText || scene?.illustrationCaption || scene?.title || "Scene illustration"),
    status: safeText(scene?.illustrationStatus).toLowerCase() || "pending",
    submittedBy: safeText(scene?.illustrationSubmittedByName),
    submittedAt: safeText(scene?.illustrationSubmittedAt),
    approvedBy: safeText(scene?.illustrationApprovedByName || scene?.illustrationReviewedByName),
    approvedAt: safeText(scene?.illustrationApprovedAt || scene?.illustrationReviewedAt),
    reviewedBy: safeText(scene?.illustrationReviewedByName || scene?.illustrationApprovedByName),
    reviewedAt: safeText(scene?.illustrationReviewedAt || scene?.illustrationApprovedAt),
    reviewReason: safeText(scene?.illustrationReviewReason),
    reviewNote: safeText(scene?.illustrationReviewNote),
  };
}

function illustrationStatusMeta(status, ui) {
  const s = safeText(status).toLowerCase();
  if (s === "approved") {
    return {
      label: ui.illustrationApproved,
      bg: "rgba(226,248,242,0.92)",
      border: "rgba(16,185,129,0.42)",
      text: "rgba(6,95,70,0.95)",
    };
  }
  if (s === "rejected") {
    return {
      label: ui.illustrationRejected,
      bg: "rgba(254,242,242,0.94)",
      border: "rgba(239,68,68,0.34)",
      text: "rgba(153,27,27,0.95)",
    };
  }
  return {
    label: ui.illustrationPending,
    bg: "rgba(255,247,237,0.94)",
    border: "rgba(245,158,11,0.34)",
    text: "rgba(124,45,18,0.95)",
  };
}

function illustrationRejectionReasonLabel(reason, ui) {
  const r = safeText(reason).toLowerCase();
  if (r === "does_not_match_scene") return ui.illustrationReasonDoesNotMatch;
  if (r === "image_quality") return ui.illustrationReasonImageQuality;
  if (r === "consent_or_copyright") return ui.illustrationReasonConsentCopyright;
  if (r === "safety_or_policy") return ui.illustrationReasonSafetyPolicy;
  if (r === "other") return ui.illustrationReasonOther;
  return safeText(reason);
}

function illustrationRejectionReasonOptions(ui) {
  return [
    { value: "does_not_match_scene", label: ui.illustrationReasonDoesNotMatch },
    { value: "image_quality", label: ui.illustrationReasonImageQuality },
    { value: "consent_or_copyright", label: ui.illustrationReasonConsentCopyright },
    { value: "safety_or_policy", label: ui.illustrationReasonSafetyPolicy },
    { value: "other", label: ui.illustrationReasonOther },
  ];
}

function SceneIllustrationDashboard({ scene, ui, onApprove, onReject, onChangeImage, workingAction }) {
  const art = safeSceneIllustration(scene);
  const sceneId = safeText(scene?._id || scene?.id);

  if (!art) {
    return (
      <div className="ns-scene-illustration-box ns-dashboard-illustration-card ns-dashboard-illustration-empty" style={{ marginTop: 10 }}>
        <div className="ns-scene-illustration-head">
          <strong>{ui.sceneIllustration}</strong>
          <Pill
            label={ui.illustrationMissing}
            bg="rgba(241,245,249,0.94)"
            border="rgba(100,116,139,0.22)"
            text="rgba(51,65,85,0.95)"
            style={{ fontSize: 12, padding: "4px 8px" }}
          />
        </div>
        <p className="ns-card-meta ns-dashboard-illustration-empty-copy">
          {ui.illustrationMissingHelp}
        </p>
        <div className="ns-illustration-change-row">
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onChangeImage) onChangeImage(scene);
            }}
            disabled={Boolean(workingAction)}
          >
            {ui.addIllustrationImage}
          </button>
        </div>
      </div>
    );
  }

  const statusMeta = illustrationStatusMeta(art.status, ui);
  const approveBusy = workingAction === `approve-illustration-${sceneId}`;
  const rejectBusy = workingAction === `reject-illustration-${sceneId}`;

  return (
    <div className="ns-scene-illustration-box ns-dashboard-illustration-card" style={{ marginTop: 10 }}>
      <div className="ns-scene-illustration-head">
        <strong>{ui.sceneIllustration}</strong>
        <Pill {...statusMeta} style={{ fontSize: 12, padding: "4px 8px" }} />
      </div>
      <figure className="ns-scene-illustration-public ns-dashboard-illustration-figure">
        <div className="ns-scene-illustration-public-frame">
          <img
            src={art.url}
            alt={art.alt || ui.sceneIllustration}
            loading="lazy"
            onError={(e) => {
              try {
                e.currentTarget.style.display = "none";
              } catch {}
            }}
          />
        </div>
        {(art.caption || art.credit) ? (
          <figcaption className="ns-scene-illustration-public-caption">
            {art.caption ? <span className="ns-scene-illustration-public-title">{art.caption}</span> : null}
            {art.credit ? (
              <span className="ns-scene-illustration-public-credit">
                {ui.illustrationCredit}: {art.credit}
              </span>
            ) : null}
          </figcaption>
        ) : null}
      </figure>
      {art.submittedBy || art.submittedAt || art.reviewedBy || art.reviewedAt ? (
        <p className="ns-card-meta ns-dashboard-illustration-meta" style={{ marginTop: 6 }}>
          {art.submittedBy ? `${ui.submittedBy} ${art.submittedBy}` : ""}
          {art.submittedAt ? `${art.submittedBy ? " · " : ""}${formatDateTime(art.submittedAt)}` : ""}
          {art.reviewedBy ? ` · ${ui.reviewedBy} ${art.reviewedBy}` : ""}
          {art.reviewedAt ? `${art.reviewedBy ? " · " : ""}${formatDateTime(art.reviewedAt)}` : ""}
        </p>
      ) : null}
      {art.status === "rejected" && (art.reviewReason || art.reviewNote) ? (
        <div className="ns-illustration-rejected-note">
          {art.reviewReason ? (
            <div><strong>{ui.illustrationReviewReason}:</strong> {illustrationRejectionReasonLabel(art.reviewReason, ui)}</div>
          ) : null}
          {art.reviewNote ? (
            <div style={{ marginTop: art.reviewReason ? 4 : 0 }}><strong>{ui.illustrationReviewNote}:</strong> {art.reviewNote}</div>
          ) : null}
        </div>
      ) : null}
      <div className="ns-illustration-change-row">
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onChangeImage) onChangeImage(scene);
          }}
          disabled={Boolean(workingAction)}
        >
          {ui.changeIllustrationImage}
        </button>
      </div>
      {art.status === "pending" ? (
        <div className="ns-illustration-review-compact">
          <details className="ns-collapsible-note">
            <summary>{ui.illustrationRulesSummary}</summary>
            <p>
              {ui.illustrationApprovalGuardrail}{" "}
              <a href={ui.illustrationPolicyHref} target="_blank" rel="noreferrer">
                {ui.illustrationPolicyLink}
              </a>
            </p>
          </details>
          <div className="ns-illustration-review-actions">
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() => onApprove(scene)}
              disabled={approveBusy || rejectBusy}
            >
              {approveBusy ? ui.working : ui.approveIllustration}
            </button>
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() => onReject(scene)}
              disabled={approveBusy || rejectBusy}
            >
              {rejectBusy ? ui.working : ui.rejectIllustration}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function safeSceneTitle(scene, ui) {
  return safeText(scene?.title || scene?.sceneTitle || "") || ui.untitledScene;
}

function safeSceneContributor(scene, ui) {
  return (
    safeText(
      scene?.authorName ||
        scene?.createdByName ||
        scene?.writerName ||
        scene?.username ||
        scene?.authorUsername ||
        scene?.createdByUsername ||
        scene?.writerUsername ||
        scene?.author?.name ||
        scene?.author?.username ||
        ""
    ) || ui.unknown
  );
}

function approvedCanonScenes(story) {
  const scenes = safeArray(story?.scenes);
  return scenes
    .filter((s) => safeText(s?.type).toLowerCase() === "canon" && safeText(s?.status).toLowerCase() === "approved")
    .slice()
    .sort((a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime());
}

function removedCanonScenes(story) {
  const scenes = safeArray(story?.scenes);
  return scenes
    .filter((s) => safeText(s?.type).toLowerCase() === "canon" && safeText(s?.status).toLowerCase() === "removed")
    .slice()
    .sort((a, b) => new Date(b?.removedAt || b?.updatedAt || 0).getTime() - new Date(a?.removedAt || a?.updatedAt || 0).getTime());
}


function sceneStableId(scene) {
  return safeText(scene?._id || scene?.id || scene?.sceneId || scene?.localId || "");
}

function normalizeSceneRatingSummaryMap(payload) {
  const raw = payload?.summaries && typeof payload.summaries === "object" ? payload.summaries : {};
  const out = {};

  Object.entries(raw).forEach(([key, value]) => {
    const sceneId = safeText(key || value?.sceneId);
    if (!sceneId) return;
    out[sceneId] = {
      sceneId,
      verifiedAverage: Number(value?.verifiedAverage || 0),
      verifiedCount: Number(value?.verifiedCount || 0),
      guestAverage: Number(value?.guestAverage || 0),
      guestCount: Number(value?.guestCount || 0),
      overallAverage: Number(value?.overallAverage || 0),
      overallCount: Number(value?.overallCount || 0),
    };
  });

  safeArray(payload?.items).forEach((value) => {
    const sceneId = safeText(value?.sceneId);
    if (!sceneId || out[sceneId]) return;
    out[sceneId] = {
      sceneId,
      verifiedAverage: Number(value?.verifiedAverage || 0),
      verifiedCount: Number(value?.verifiedCount || 0),
      guestAverage: Number(value?.guestAverage || 0),
      guestCount: Number(value?.guestCount || 0),
      overallAverage: Number(value?.overallAverage || 0),
      overallCount: Number(value?.overallCount || 0),
    };
  });

  return out;
}

function readerInterestMeta(summary, ui) {
  const count = Number(summary?.overallCount || 0);
  const average = Number(summary?.overallAverage || 0);

  if (!count || !average) {
    return {
      label: ui.readerInterestWaiting,
      className: "is-empty",
    };
  }

  if (average >= 4.5) return { label: ui.readerInterestExcellent, className: "is-excellent" };
  if (average >= 4) return { label: ui.readerInterestStrong, className: "is-strong" };
  if (average >= 3) return { label: ui.readerInterestSteady, className: "is-steady" };
  return { label: ui.readerInterestLow, className: "is-low" };
}

function ratingCountLabel(count, ui) {
  const n = Number(count || 0);
  const template = n === 1 ? ui.readerRatingSingular : ui.readerRatingPlural;
  return String(template || "{count} ratings").replace("{count}", String(n));
}

function formatDashboardRating(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(1);
}

function SceneRatingSummaryPill({ summary, loading, ui }) {
  const count = Number(summary?.overallCount || 0);
  const average = Number(summary?.overallAverage || 0);
  const verifiedCount = Number(summary?.verifiedCount || 0);
  const guestCount = Number(summary?.guestCount || 0);
  const meta = readerInterestMeta(summary, ui);

  const title = count
    ? `${ui.readerVerifiedShort}: ${verifiedCount} · ${ui.readerGuestShort}: ${guestCount}`
    : ui.readerInterestWaiting;
  const scoreText = loading && !summary ? "…" : count > 0 ? `${formatDashboardRating(average)} ★` : "—";

  return (
    <div className={`ns-dashboard-scene-rating-summary ${meta.className}`} title={title} aria-label={ui.readerInterest}>
      <span className="ns-dashboard-scene-rating-label">{ui.readerInterest}</span>
      <span className="ns-dashboard-scene-rating-score">{scoreText}</span>
      {count > 0 ? <span className="ns-dashboard-scene-rating-count">{ratingCountLabel(count, ui)}</span> : null}
      {count > 0 ? <span className="ns-dashboard-scene-rating-level">{meta.label}</span> : null}
    </div>
  );
}


function normalizeSceneReactionSummaryMap(payload) {
  const out = {};

  function emptyCounts() {
    const counts = {};
    SCENE_REACTION_CODES.forEach((code) => {
      counts[code] = 0;
    });
    return counts;
  }

  function add(value, key = "") {
    const sceneId = safeText(value?.sceneId || key);
    if (!sceneId) return;
    const counts = emptyCounts();
    const rawCounts = value?.reactionCounts && typeof value.reactionCounts === "object" ? value.reactionCounts : {};
    SCENE_REACTION_CODES.forEach((code) => {
      counts[code] = Number(rawCounts?.[code] || 0);
    });
    out[sceneId] = {
      sceneId,
      reactionCounts: counts,
      totalCount: Number(value?.totalCount || Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0)),
    };
  }

  const raw = payload?.summaries && typeof payload.summaries === "object" ? payload.summaries : {};
  Object.entries(raw).forEach(([key, value]) => add(value, key));
  safeArray(payload?.items).forEach((value) => add(value));

  return out;
}

function readerSignalCountLabel(count, ui) {
  const n = Number(count || 0);
  const template = n === 1 ? ui.readerSignalsSingular : ui.readerSignalsPlural;
  return String(template || "{count} signals").replace("{count}", String(n));
}

function sceneReactionLabel(code, ui) {
  const labels = ui.readerReactionLabels && typeof ui.readerReactionLabels === "object" ? ui.readerReactionLabels : {};
  return safeText(labels?.[code]) || code;
}

function SceneReactionSummaryPill({ summary, loading, ui }) {
  const counts = summary?.reactionCounts && typeof summary.reactionCounts === "object" ? summary.reactionCounts : {};
  const total = Number(summary?.totalCount || Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0));
  const items = SCENE_REACTION_CODES
    .map((code) => ({ code, count: Number(counts?.[code] || 0) }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count || SCENE_REACTION_CODES.indexOf(a.code) - SCENE_REACTION_CODES.indexOf(b.code));

  const title = total > 0
    ? items.map((item) => `${sceneReactionLabel(item.code, ui)}: ${item.count}`).join(" · ")
    : ui.readerSignalsWaiting;

  return (
    <div
      className={`ns-dashboard-scene-reaction-summary ${total > 0 ? "has-signals" : "is-empty"}`}
      title={title}
      aria-label={ui.readerSignals}
    >
      <span className="ns-dashboard-scene-reaction-label">{ui.readerSignals}</span>
      {loading && !summary ? (
        <span className="ns-dashboard-scene-reaction-empty">…</span>
      ) : total > 0 ? (
        <>
          <span className="ns-dashboard-scene-reaction-total">{readerSignalCountLabel(total, ui)}</span>
          <span className="ns-dashboard-scene-reaction-chips">
            {items.map((item) => (
              <span className="ns-dashboard-scene-reaction-chip" key={item.code}>
                {item.count} {sceneReactionLabel(item.code, ui)}
              </span>
            ))}
          </span>
        </>
      ) : (
        <span className="ns-dashboard-scene-reaction-empty">{ui.readerSignalsWaiting}</span>
      )}
    </div>
  );
}


function normalizeSceneCompletionSummaryMap(payload) {
  const raw = payload?.summaries && typeof payload.summaries === "object" ? payload.summaries : {};
  const out = {};

  function add(value, key = "") {
    const sceneId = safeText(value?.sceneId || key);
    if (!sceneId) return;
    out[sceneId] = {
      sceneId,
      sceneNumber: Number(value?.sceneNumber || 0) || null,
      title: safeText(value?.title || ""),
      openedReaders: Number(value?.openedReaders || 0),
      completedReaders: Number(value?.completedReaders || 0),
      nextReaders: Number(value?.nextReaders || 0),
      previousReaders: Number(value?.previousReaders || 0),
      returnedReaders: Number(value?.returnedReaders || 0),
      openedEvents: Number(value?.openedEvents || 0),
      completedEvents: Number(value?.completedEvents || 0),
      nextClicks: Number(value?.nextClicks || 0),
      previousClicks: Number(value?.previousClicks || 0),
      returnEvents: Number(value?.returnEvents || 0),
      completionRate: Number(value?.completionRate || 0),
      continueRate: Number(value?.continueRate || 0),
      returnRate: Number(value?.returnRate || 0),
      lastAction: safeText(value?.lastAction || ""),
      lastActionAt: safeText(value?.lastActionAt || ""),
    };
  }

  Object.entries(raw).forEach(([key, value]) => add(value, key));
  safeArray(payload?.items).forEach((value) => add(value));

  return out;
}

function readerMovementMeta(summary, ui) {
  const opened = Number(summary?.openedReaders || 0);
  const completed = Number(summary?.completedReaders || 0);
  const continued = Number(summary?.nextReaders || 0);
  const returned = Number(summary?.returnedReaders || 0);
  const rate = opened > 0 ? completed / opened : 0;

  if (!opened) return { label: ui.readerMovementWaiting, className: "is-empty" };
  if (returned > 0) return { label: ui.readerMovementReturn, className: "is-return" };
  if (opened < 2 && completed > 0) return { label: ui.readerMovementEarly, className: "is-early" };
  if (rate >= 0.75 && continued > 0) return { label: ui.readerMovementStrong, className: "is-strong" };
  if (rate >= 0.5) return { label: ui.readerMovementSteady, className: "is-steady" };
  return { label: ui.readerMovementWatch, className: "is-watch" };
}

function readerMovementCountLabel(summary, ui) {
  const opened = Number(summary?.openedReaders || 0);
  const completed = Number(summary?.completedReaders || 0);
  const continued = Number(summary?.nextReaders || 0);
  const returned = Number(summary?.returnedReaders || 0);
  const base = `${opened} ${ui.readerMovementOpened} · ${completed} ${ui.readerMovementCompleted} · ${continued} ${ui.readerMovementContinued}`;
  return returned > 0 ? `${base} · ${returned} ${ui.readerMovementReturned}` : base;
}

function SceneCompletionSummaryPill({ summary, loading, ui }) {
  const opened = Number(summary?.openedReaders || 0);
  const completed = Number(summary?.completedReaders || 0);
  const continued = Number(summary?.nextReaders || 0);
  const returned = Number(summary?.returnedReaders || 0);
  const meta = readerMovementMeta(summary, ui);
  const title = opened
    ? `${ui.readerMovementOpened}: ${opened} · ${ui.readerMovementCompleted}: ${completed} · ${ui.readerMovementContinued}: ${continued} · ${ui.readerMovementReturned}: ${returned}`
    : ui.readerMovementWaiting;
  const scoreText = loading && !summary ? "…" : opened > 0 ? `${completed}/${opened}` : "—";

  return (
    <div className={`ns-dashboard-scene-completion-summary ${meta.className}`} title={title} aria-label={ui.readerMovement}>
      <span className="ns-dashboard-scene-completion-label">{ui.readerMovement}</span>
      <span className="ns-dashboard-scene-completion-score">{scoreText}</span>
      {opened > 0 ? <span className="ns-dashboard-scene-completion-count">{readerMovementCountLabel(summary, ui)}</span> : null}
      {opened > 0 ? <span className="ns-dashboard-scene-completion-level">{meta.label}</span> : null}
    </div>
  );
}

function isProposalSceneType(scene) {
  const type = safeText(scene?.type).toLowerCase();
  // Backend historically stores proposed scenes as "proposed".
  // Some frontend/dashboard language uses "proposal". Treat both as the same lane.
  return type === "proposal" || type === "proposed";
}

function pendingProposalScenes(story) {
  const scenes = safeArray(story?.scenes);
  return scenes
    .filter((s) => isProposalSceneType(s) && safeText(s?.status).toLowerCase() === "pending")
    .slice()
    .sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
}

function rejectedProposalScenes(story) {
  const scenes = safeArray(story?.scenes);
  return scenes
    .filter((s) => isProposalSceneType(s) && safeText(s?.status).toLowerCase() === "rejected")
    .slice()
    .sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
}

function countReadersFromStory(story) {
  return (
    Number(story?.tracking?.readers) ||
    Number(story?.readerCount) ||
    Number(story?.stats?.readers) ||
    Number(story?.analytics?.readers) ||
    0
  );
}

function countViewsFromStory(story) {
  return (
    Number(story?.tracking?.opens) ||
    Number(story?.openCount) ||
    Number(story?.stats?.opens) ||
    Number(story?.analytics?.opens) ||
    0
  );
}

function storyAuthorInfo(story) {
  const name =
    story?.authorName ||
    story?.author ||
    story?.createdByName ||
    story?.ownerName ||
    story?.writerName ||
    "";
  const username =
    story?.authorUsername ||
    story?.createdByUsername ||
    story?.ownerUsername ||
    story?.writerUsername ||
    story?.username ||
    "";
  const authorId =
    story?.authorId ||
    story?.createdBy ||
    story?.ownerId ||
    story?.writerId ||
    story?.userId ||
    story?.author?._id ||
    story?.author?.id ||
    "";
  const email =
    story?.authorEmail ||
    story?.author?.email ||
    story?.ownerEmail ||
    story?.createdByEmail ||
    "";
  return {
    name: safeText(name),
    username: safeText(username),
    authorId: safeText(authorId),
    email: safeText(email),
  };
}

function userIdentity(user) {
  return {
    id: safeText(user?._id || user?.id),
    username: safeText(user?.username || user?.userName),
    name: safeText(user?.name || user?.fullName),
    email: safeText(user?.email),
  };
}

function isOwnedByCurrentUser(story, user) {
  const s = storyAuthorInfo(story);
  const u = userIdentity(user);
  if (u.id && s.authorId && u.id === s.authorId) return true;
  if (u.email && s.email && u.email.toLowerCase() === s.email.toLowerCase()) return true;
  if (u.username && s.username && u.username.toLowerCase() === s.username.toLowerCase()) return true;
  if (u.name && s.name && u.name.toLowerCase() === s.name.toLowerCase()) return true;
  return false;
}

function guessDescription(story) {
  return safeText(
    story?.description ||
      story?.storyDescription ||
      story?.blurb ||
      story?.summary ||
      story?.synopsis ||
      story?.logline ||
      story?.meta?.description ||
      ""
  );
}

function countStoryContributors(story, ui) {
  const scenes = safeArray(story?.scenes);
  const names = scenes
    .map((scene) => safeSceneContributor(scene, ui))
    .filter(Boolean)
    .map((name) => safeText(name));
  return Array.from(new Set(names)).length;
}

function storyLastActivityAt(story) {
  const scenes = safeArray(story?.scenes);
  const candidates = [story?.updatedAt, story?.lastUpdatedAt, story?.canonUpdatedAt, story?.createdAt];
  scenes.forEach((scene) => {
    candidates.push(scene?.updatedAt, scene?.createdAt, scene?.decisionAt);
  });
  let best = 0;
  candidates.forEach((value) => {
    const t = new Date(value || 0).getTime();
    if (Number.isFinite(t) && t > best) best = t;
  });
  return best ? new Date(best).toISOString() : "";
}

function storyPulseLabel(story, lang = "en") {
  const iso = storyLastActivityAt(story);
  const isFR = String(lang || "en").toLowerCase() === "fr";
  if (!iso) return isFR ? "Calme" : "Quiet";
  const diffHours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (diffHours < 24) return isFR ? "Actif maintenant" : "Active now";
  if (diffHours < 24 * 7) return isFR ? "Actif récemment" : "Recently active";
  return isFR ? "Calme" : "Quiet";
}

function storyDirectionLabel(story, lang = "en") {
  const isFR = String(lang || "en").toLowerCase() === "fr";
  const proposalsOpen = !!story?.proposalsOpen;
  const scenes = safeArray(story?.scenes);
  const pending = scenes.filter((s) => {
    const type = safeText(s?.type).toLowerCase();
    const status = safeText(s?.status).toLowerCase();
    return (type === "proposal" || type === "proposed") && (status === "pending" || !status);
  }).length;
  if (proposalsOpen && pending > 0) return isFR ? "Décision de l’auteur attendue" : "Awaiting author decision";
  if (proposalsOpen) return isFR ? "Propositions ouvertes" : "Proposal round open";
  return isFR ? "Canon en progression" : "Canon building";
}

function parseStoryIdFromUrl() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    return safeText(qs.get("storyId") || qs.get("id"));
  } catch {
    return "";
  }
}

function setStoryIdInUrl(storyId) {
  try {
    const id = safeText(storyId);
    const base = "/story-dashboard";
    if (!id) {
      window.history.replaceState({}, "", base);
      return;
    }
    window.history.replaceState({}, "", `${base}?storyId=${encodeURIComponent(id)}`);
  } catch {}
}

function normalizeForSearch(v) {
  return safeText(v).toLowerCase();
}

function storyMatchesQuery(story, query) {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const author = storyAuthorInfo(story);
  const hay = [
    story?.title,
    story?.forum,
    story?.writingMode,
    story?.description,
    story?.summary,
    story?.synopsis,
    author.name,
    author.username,
  ]
    .map(normalizeForSearch)
    .join(" ");
  return hay.includes(q);
}

function Skel({ w = "100%", h = 12, r = 10, style }) {
  return <div className="ns-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function SkeletonListItem() {
  return (
    <div className="ns-card ns-card-soft" style={{ padding: 12, marginBottom: 10 }}>
      <Skel w="58%" h={13} />
      <div style={{ marginTop: 10 }}>
        <Skel w="80%" h={11} />
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
        <Skel w="120px" h={28} r={999} />
        <Skel w="92px" h={28} r={999} />
      </div>
    </div>
  );
}

function SkeletonRightPanel() {
  return (
    <div className="ns-card ns-card-soft" style={{ padding: 16 }}>
      <Skel w="48%" h={20} r={12} />
      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Skel w="170px" h={30} r={999} />
        <Skel w="150px" h={30} r={999} />
        <Skel w="130px" h={30} r={999} />
      </div>
      <div style={{ marginTop: 16 }}>
        <Skel w="96%" h={12} />
        <div style={{ marginTop: 10 }}>
          <Skel w="88%" h={12} />
        </div>
        <div style={{ marginTop: 10 }}>
          <Skel w="84%" h={12} />
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <div className="ns-skel" style={{ height: 160, borderRadius: 16 }} />
      </div>
    </div>
  );
}

function Pill({ label, bg, border, text, style }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color: text,
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: "0.2px",
        ...style,
      }}
    >
      {label}
    </span>
  );
}

function ModePill({ mode, ui, style }) {
  const m = modeMeta(mode, ui);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: m.bg,
        border: `1px solid ${m.border}`,
        color: m.text,
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: "0.2px",
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: m.dot,
          boxShadow: "0 0 0 3px rgba(15, 23, 42, 0.04)",
        }}
      />
      {m.label}
    </span>
  );
}

function sectionTitle(text) {
  return (
    <h2 className="ns-section-title" style={{ marginBottom: 10 }}>
      {text}
    </h2>
  );
}

function countManuscriptWords(text) {
  const t = safeText(text).replace(/\s+/g, " ").trim();
  if (!t) return 0;
  return t.split(" ").filter(Boolean).length;
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function sanitizeFileName(value) {
  const cleaned = safeText(value)
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "NextScenes Manuscript";
}

function crc32Bytes(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(n) {
  return [n & 255, (n >>> 8) & 255];
}

function u32(n) {
  return [n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255];
}

function encodeUtf8(text) {
  return new TextEncoder().encode(String(text || ""));
}

function createZipBlob(files, mimeType) {
  const chunks = [];
  const central = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encodeUtf8(file.name);
    const dataBytes = file.bytes instanceof Uint8Array ? file.bytes : encodeUtf8(file.content || "");
    const crc = crc32Bytes(dataBytes);
    const size = dataBytes.length;

    const localHeader = new Uint8Array([
      ...u32(0x04034b50), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0),
      ...u32(crc), ...u32(size), ...u32(size), ...u16(nameBytes.length), ...u16(0),
    ]);
    chunks.push(localHeader, nameBytes, dataBytes);

    const centralHeader = new Uint8Array([
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0x0800), ...u16(0), ...u16(0), ...u16(0),
      ...u32(crc), ...u32(size), ...u32(size), ...u16(nameBytes.length), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0), ...u32(0), ...u32(offset),
    ]);
    central.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + dataBytes.length;
  });

  const centralStart = offset;
  let centralSize = 0;
  central.forEach((part) => { centralSize += part.length; chunks.push(part); });

  chunks.push(new Uint8Array([
    ...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(files.length), ...u16(files.length),
    ...u32(centralSize), ...u32(centralStart), ...u16(0),
  ]));

  return new Blob(chunks, { type: mimeType });
}

function docxParagraph(text, style = "Normal") {
  const clean = String(text || "").replace(/\r\n/g, "\n");
  const stylePart = style ? `<w:pPr><w:pStyle w:val="${escapeXml(style)}"/></w:pPr>` : "";
  const runs = clean.split("\n").map((line, index) => {
    const br = index === 0 ? "" : "<w:br/>";
    return `<w:r>${br}<w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r>`;
  }).join("");
  return `<w:p>${stylePart}${runs}</w:p>`;
}

function docxBlank() { return '<w:p><w:pPr><w:pStyle w:val="NoSpacing"/></w:pPr></w:p>'; }
function docxPageBreak() { return '<w:p><w:r><w:br w:type="page"/></w:r></w:p>'; }

function docxCenteredParagraph(text, style = "Caption") {
  const stylePart = style ? `<w:pStyle w:val="${escapeXml(style)}"/>` : "";
  return `<w:p><w:pPr>${stylePart}<w:jc w:val="center"/></w:pPr><w:r><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
}

function docxFooterXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:p><w:pPr><w:pStyle w:val="Footer"/><w:jc w:val="center"/></w:pPr><w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r></w:p></w:ftr>`;
}

function docxImageParagraph(image) {
  const cx = Math.max(1, Math.round(Number(image?.cx || 1)));
  const cy = Math.max(1, Math.round(Number(image?.cy || 1)));
  const docPrId = Math.max(1, Math.round(Number(image?.docPrId || 1)));
  const name = escapeXml(image?.name || `Scene illustration ${docPrId}`);
  const alt = escapeXml(image?.alt || image?.caption || "Scene illustration");
  const relId = escapeXml(image?.relId || "");

  if (!relId) return "";

  return `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${cx}" cy="${cy}"/><wp:effectExtent l="0" t="0" r="0" b="0"/><wp:docPr id="${docPrId}" name="${name}" descr="${alt}"/><wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/></wp:cNvGraphicFramePr><a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:nvPicPr><pic:cNvPr id="${docPrId}" name="${name}" descr="${alt}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="${relId}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`;
}

function docxTableParagraph(value, bold = false) {
  return `<w:p><w:pPr><w:pStyle w:val="TableText"/></w:pPr><w:r>${bold ? "<w:rPr><w:b/></w:rPr>" : ""}<w:t xml:space="preserve">${escapeXml(value)}</w:t></w:r></w:p>`;
}

function docxLabelValueTable(rows) {
  const cellMargins = '<w:tcMar><w:top w:w="90" w:type="dxa"/><w:left w:w="110" w:type="dxa"/><w:bottom w:w="90" w:type="dxa"/><w:right w:w="110" w:type="dxa"/></w:tcMar>';
  const labelCell = (value) => `<w:tc><w:tcPr><w:tcW w:w="3000" w:type="dxa"/>${cellMargins}</w:tcPr>${docxTableParagraph(value, true)}</w:tc>`;
  const valueCell = (value) => `<w:tc><w:tcPr><w:tcW w:w="6200" w:type="dxa"/>${cellMargins}</w:tcPr>${docxTableParagraph(value)}</w:tc>`;
  return `<w:tbl><w:tblPr><w:tblW w:w="9200" w:type="dxa"/><w:tblLook w:firstRow="0" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/><w:tblBorders><w:top w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/><w:left w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/><w:bottom w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/><w:right w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/><w:insideH w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/><w:insideV w:val="single" w:sz="4" w:space="0" w:color="D6DEE8"/></w:tblBorders></w:tblPr>${rows.map((row) => `<w:tr>${labelCell(row[0])}${valueCell(row[1])}</w:tr>`).join("")}</w:tbl>`;
}

function docxTable(rows) {
  const cellMargins = '<w:tcMar><w:top w:w="90" w:type="dxa"/><w:left w:w="110" w:type="dxa"/><w:bottom w:w="90" w:type="dxa"/><w:right w:w="110" w:type="dxa"/></w:tcMar>';
  const cell = (value, bold = false) => `<w:tc><w:tcPr><w:tcW w:w="2400" w:type="dxa"/>${cellMargins}</w:tcPr>${docxTableParagraph(value, bold)}</w:tc>`;
  return `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/><w:tblBorders><w:top w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:left w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:bottom w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:right w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:insideH w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/><w:insideV w:val="single" w:sz="4" w:space="0" w:color="CBD5E1"/></w:tblBorders></w:tblPr>${rows.map((row, rowIndex) => `<w:tr>${row.map((v) => cell(v, rowIndex === 0)).join("")}</w:tr>`).join("")}</w:tbl>`;
}

function buildContributorRows(canonScenes, ui) {
  const map = new Map();
  let totalWords = 0;
  canonScenes.forEach((scene) => {
    const name = safeSceneContributor(scene, ui);
    const words = countManuscriptWords(safeSceneText(scene));
    totalWords += words;
    const current = map.get(name) || { name, scenes: 0, words: 0 };
    current.scenes += 1;
    current.words += words;
    map.set(name, current);
  });
  return Array.from(map.values()).map((row) => ({
    ...row,
    percentage: totalWords > 0 ? ((row.words / totalWords) * 100).toFixed(1) : "0.0",
  })).sort((a, b) => b.words - a.words || a.name.localeCompare(b.name));
}

function isApprovedSceneIllustration(scene) {
  const art = safeSceneIllustration(scene);
  return !!(art && art.url && safeText(art.status).toLowerCase() === "approved");
}

function getApprovedSceneIllustration(scene) {
  return isApprovedSceneIllustration(scene) ? safeSceneIllustration(scene) : null;
}

function extensionFromContentType(contentType, fallback = "jpg") {
  const ct = safeText(contentType).toLowerCase();
  if (ct.includes("png")) return "png";
  if (ct.includes("webp")) return "webp";
  if (ct.includes("gif")) return "gif";
  if (ct.includes("jpeg") || ct.includes("jpg")) return "jpg";
  return fallback || "jpg";
}

function contentTypeFromExtension(extension) {
  const ext = safeText(extension).toLowerCase();
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  if (ext === "gif") return "image/gif";
  return "image/jpeg";
}

function extensionFromUrl(url) {
  try {
    const pathname = new URL(String(url || "")).pathname.toLowerCase();
    const match = pathname.match(/\.([a-z0-9]+)$/);
    if (!match) return "";
    const ext = match[1];
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return ext === "jpeg" ? "jpg" : ext;
  } catch {}
  return "";
}

function blobToUint8Array(blob) {
  return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
}

function readBlobAsImageDimensions(blob) {
  return new Promise((resolve) => {
    if (typeof Image === "undefined" || typeof URL === "undefined") {
      resolve({ width: 1200, height: 800 });
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const width = Number(img.naturalWidth || img.width || 1200);
      const height = Number(img.naturalHeight || img.height || 800);
      URL.revokeObjectURL(objectUrl);
      resolve({ width: width > 0 ? width : 1200, height: height > 0 ? height : 800 });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: 1200, height: 800 });
    };
    img.src = objectUrl;
  });
}

async function convertImageBlobToPng(blob) {
  if (typeof document === "undefined") return null;

  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not prepare illustration for Word export."));
      image.src = objectUrl;
    });

    const width = Number(img.naturalWidth || img.width || 1200) || 1200;
    const height = Number(img.naturalHeight || img.height || 800) || 800;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);

    const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!pngBlob) return null;
    return { blob: pngBlob, width, height, extension: "png", contentType: "image/png" };
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function docxImageSize(width, height) {
  const rawWidth = Number(width || 1200) || 1200;
  const rawHeight = Number(height || 800) || 800;
  const maxCx = Math.round(5.85 * 914400);
  const maxCy = Math.round(4.4 * 914400);
  let cx = maxCx;
  let cy = Math.round(cx * (rawHeight / rawWidth));

  if (cy > maxCy) {
    cy = maxCy;
    cx = Math.round(cy * (rawWidth / rawHeight));
  }

  return { cx, cy };
}

async function fetchDocxIllustrationAsset({ art, sceneIndex, imageIndex }) {
  const response = await fetch(art.url, { method: "GET", mode: "cors", credentials: "omit" });
  if (!response.ok) throw new Error(`Image request failed with HTTP ${response.status}.`);

  let blob = await response.blob();
  let extension = extensionFromContentType(blob.type, extensionFromUrl(art.url) || "jpg");
  let contentType = contentTypeFromExtension(extension);
  let dimensions = await readBlobAsImageDimensions(blob);

  // Word support for WEBP is inconsistent. Convert WEBP to PNG when the browser can decode it.
  if (extension === "webp") {
    const converted = await convertImageBlobToPng(blob);
    if (converted?.blob) {
      blob = converted.blob;
      extension = converted.extension;
      contentType = converted.contentType;
      dimensions = { width: converted.width, height: converted.height };
    }
  }

  const bytes = await blobToUint8Array(blob);
  const size = docxImageSize(dimensions.width, dimensions.height);
  const mediaName = `scene-illustration-${imageIndex}.${extension}`;

  return {
    art,
    sceneIndex,
    imageIndex,
    relId: `rIdImage${imageIndex}`,
    docPrId: imageIndex,
    name: `Scene illustration ${sceneIndex + 1}`,
    alt: art.alt || art.caption || "Scene illustration",
    caption: art.caption,
    credit: art.credit,
    extension,
    contentType,
    mediaName,
    mediaPath: `word/media/${mediaName}`,
    relationshipTarget: `media/${mediaName}`,
    bytes,
    cx: size.cx,
    cy: size.cy,
  };
}

async function prepareDocxIllustrationAssets(canonScenes) {
  const imageBySceneIndex = new Map();
  const failedBySceneIndex = new Map();
  const mediaFiles = [];
  let imageIndex = 1;

  for (let sceneIndex = 0; sceneIndex < canonScenes.length; sceneIndex += 1) {
    const art = getApprovedSceneIllustration(canonScenes[sceneIndex]);
    if (!art) continue;

    try {
      // eslint-disable-next-line no-await-in-loop
      const asset = await fetchDocxIllustrationAsset({ art, sceneIndex, imageIndex });
      imageBySceneIndex.set(sceneIndex, asset);
      mediaFiles.push({ name: asset.mediaPath, bytes: asset.bytes, contentType: asset.contentType, extension: asset.extension });
      imageIndex += 1;
    } catch (err) {
      failedBySceneIndex.set(sceneIndex, { art, error: err });
    }
  }

  return { imageBySceneIndex, failedBySceneIndex, mediaFiles };
}

async function buildStoryManuscriptDocxBlob({ story, canonScenes, ui, lang, includeContributorAppendix = true }) {
  const isFR = lang === "fr";
  const title = safeText(story?.title) || (isFR ? "Storyline sans titre" : "Untitled Storyline");
  const author = storyAuthorInfo(story);
  const authorName = author.name || author.username || safeText(story?.author) || ui.unknown;
  const generatedAt = formatDateTime(new Date().toISOString(), lang);
  const description = guessDescription(story);
  const synopsis = safeText(story?.synopsis || story?.summary || "");
  const modeLabel = modeMeta(story?.writingMode, ui).label;
  const totalWords = canonScenes.reduce((sum, scene) => sum + countManuscriptWords(safeSceneText(scene)), 0);
  const contributorRows = buildContributorRows(canonScenes, ui);
  const approvedIllustrationCount = canonScenes.filter(isApprovedSceneIllustration).length;
  const { imageBySceneIndex, failedBySceneIndex, mediaFiles } = await prepareDocxIllustrationAssets(canonScenes);
  const embeddedIllustrationCount = imageBySceneIndex.size;
  const body = [];

  body.push(docxParagraph(title, "Title"));
  body.push(docxParagraph(isFR ? "Manuscrit de travail généré par NextScenes" : "Working manuscript generated by NextScenes", "Subtitle"));
  body.push(docxBlank());
  body.push(docxBlank());
  body.push(docxParagraph(isFR ? "Informations du manuscrit" : "Manuscript Information", "Heading1"));
  body.push(docxLabelValueTable([
    [isFR ? "Auteur" : "Author", authorName],
    [isFR ? "Généré par" : "Generated by", "NextScenes"],
    [isFR ? "Date de génération" : "Generated", generatedAt],
    [ui.forum, safeText(story?.forum) || ui.unknown],
    [ui.writingMode, modeLabel],
    [isFR ? "Scènes canon approuvées" : "Approved Canon scenes", String(canonScenes.length)],
    [isFR ? "Illustrations approuvées" : "Approved illustrations", `${embeddedIllustrationCount}/${approvedIllustrationCount}`],
    [isFR ? "Nombre approximatif de mots" : "Approximate word count", String(totalWords)],
    [isFR ? "Annexe des contributions" : "Contributor appendix", includeContributorAppendix ? (isFR ? "Incluse" : "Included") : (isFR ? "Non incluse" : "Not included")],
  ]));
  body.push(docxBlank());
  body.push(docxParagraph(isFR ? "Note" : "Note", "Heading1"));
  body.push(docxParagraph(isFR ? "Ce manuscrit contient uniquement les scènes approuvées du Canon au moment de son exportation. Les propositions en attente, les propositions rejetées, les commentaires et les notes privées ne sont pas inclus. Les illustrations de scènes sont incluses uniquement lorsqu’elles sont approuvées." : "This manuscript contains approved Canon scenes only at the time of export. Pending proposals, rejected proposals, comments, and private notes are not included. Scene illustrations are included only when they are approved.", "BodyText"));

  if (description) { body.push(docxBlank()); body.push(docxParagraph(ui.description, "Heading1")); body.push(docxParagraph(description, "BodyText")); }
  if (synopsis) { body.push(docxBlank()); body.push(docxParagraph(ui.synopsis, "Heading1")); body.push(docxParagraph(synopsis, "BodyText")); }

  body.push(docxPageBreak());
  body.push(docxParagraph(isFR ? "Manuscrit" : "Manuscript", "Heading1"));
  body.push(docxBlank());

  canonScenes.forEach((scene, index) => {
    // Keep scenes continuous in the working manuscript.
    // A hard page break before every scene created too much empty space in Word/LibreOffice.
    if (index > 0) body.push(docxBlank());
    const sceneTitle = safeSceneTitle(scene, ui);
    const contributor = safeSceneContributor(scene, ui);
    const sceneDate = formatDateTime(scene?.createdAt || scene?.updatedAt || "", lang);
    const image = imageBySceneIndex.get(index);
    const failedImage = failedBySceneIndex.get(index);
    body.push(docxParagraph(`${ui.scene} ${index + 1}`, "Heading1"));
    body.push(docxParagraph(sceneTitle, "Heading2"));
    body.push(docxParagraph(`${ui.contributor}: ${contributor}${sceneDate ? ` | ${sceneDate}` : ""}`, "Subtitle"));
    body.push(docxBlank());

    if (image) {
      body.push(docxImageParagraph(image));
      if (image.caption) body.push(docxCenteredParagraph(image.caption, "Caption"));
      if (image.credit) body.push(docxCenteredParagraph(`${isFR ? "Crédit" : "Credit"}: ${image.credit}`, "Credit"));
      body.push(docxBlank());
    } else if (failedImage) {
      body.push(docxParagraph(isFR ? "[Illustration approuvée non intégrée dans ce fichier Word. Vérifiez que le lien d’image public est accessible.]" : "[Approved illustration was not embedded in this Word file. Check that the public image link is accessible.]", "Subtitle"));
      body.push(docxBlank());
    }

    const blocks = safeSceneText(scene).replace(/\r\n/g, "\n").split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);
    if (blocks.length) blocks.forEach((block) => body.push(docxParagraph(block, "BodyText")));
    else body.push(docxParagraph(isFR ? "[Aucun texte de scène]" : "[No scene text]", "Subtitle"));
  });

  if (includeContributorAppendix) {
    body.push(docxPageBreak());
    body.push(docxParagraph(isFR ? "Annexe des contributions" : "Contributor Appendix", "Heading1"));
    body.push(docxParagraph(isFR ? "Ce registre résume les contributions acceptées dans le Canon au moment de la génération du manuscrit." : "This record summarizes accepted Canon contributions at the time this manuscript was generated.", "BodyText"));
    body.push(docxBlank());
    body.push(docxTable([
      [ui.contributor, isFR ? "Scènes acceptées" : "Accepted scenes", isFR ? "Mots acceptés" : "Accepted words", isFR ? "% du Canon" : "% of Canon"],
      ...contributorRows.map((row) => [row.name, String(row.scenes), String(row.words), `${row.percentage}%`]),
    ]));
  }

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"><w:body>${body.join("")}<w:sectPr><w:footerReference w:type="default" r:id="rIdFooter1"/><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/><w:cols w:space="720"/><w:docGrid w:linePitch="360"/></w:sectPr></w:body></w:document>`;
  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr></w:rPrDefault><w:pPrDefault><w:pPr><w:spacing w:after="120" w:line="276" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults><w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:pPr><w:spacing w:after="120" w:line="276" w:lineRule="auto"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="NoSpacing"><w:name w:val="No Spacing"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:pPr><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="BodyText"><w:name w:val="Body Text"/><w:basedOn w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:pPr><w:jc w:val="both"/><w:spacing w:before="0" w:after="120" w:line="360" w:lineRule="auto"/><w:ind w:firstLine="720"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:b/><w:sz w:val="52"/><w:szCs w:val="52"/></w:rPr><w:pPr><w:jc w:val="center"/><w:spacing w:before="1440" w:after="260"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Subtitle"><w:name w:val="Subtitle"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:i/><w:color w:val="475569"/><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr><w:pPr><w:jc w:val="center"/><w:spacing w:after="140"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:b/><w:sz w:val="32"/><w:szCs w:val="32"/></w:rPr><w:pPr><w:keepNext/><w:spacing w:before="360" w:after="160"/><w:jc w:val="left"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:b/><w:sz w:val="28"/><w:szCs w:val="28"/></w:rPr><w:pPr><w:keepNext/><w:spacing w:before="80" w:after="140"/><w:jc w:val="left"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Caption"><w:name w:val="Caption"/><w:basedOn w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:i/><w:color w:val="334155"/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:pPr><w:jc w:val="center"/><w:spacing w:before="80" w:after="60"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Credit"><w:name w:val="Credit"/><w:basedOn w:val="Caption"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:i/><w:color w:val="64748B"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:pPr><w:jc w:val="center"/><w:spacing w:before="0" w:after="160"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="TableText"><w:name w:val="Table Text"/><w:basedOn w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr><w:pPr><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/><w:jc w:val="left"/></w:pPr></w:style><w:style w:type="paragraph" w:styleId="Footer"><w:name w:val="Footer"/><w:basedOn w:val="Normal"/><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/><w:color w:val="64748B"/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:pPr><w:spacing w:before="0" w:after="0"/><w:jc w:val="center"/></w:pPr></w:style></w:styles>`;
  const imageRelationships = mediaFiles.map((file, index) => `<Relationship Id="rIdImage${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/${escapeXml(file.name.split("/").pop())}"/>`).join("");
  const imageDefaults = Array.from(new Set(mediaFiles.map((file) => safeText(file.extension).toLowerCase()).filter(Boolean)))
    .map((extension) => `<Default Extension="${escapeXml(extension)}" ContentType="${escapeXml(contentTypeFromExtension(extension))}"/>`)
    .join("");

  return createZipBlob([
    { name: "[Content_Types].xml", content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/>${imageDefaults}<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/></Types>` },
    { name: "_rels/.rels", content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>` },
    { name: "word/_rels/document.xml.rels", content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rIdFooter1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>${imageRelationships}</Relationships>` },
    { name: "word/document.xml", content: documentXml },
    { name: "word/styles.xml", content: stylesXml },
    { name: "word/footer1.xml", content: docxFooterXml() },
    ...mediaFiles,
  ], "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
}

async function downloadStoryManuscriptDocx({ story, canonScenes, ui, lang, includeContributorAppendix = true }) {
  const blob = await buildStoryManuscriptDocxBlob({ story, canonScenes, ui, lang, includeContributorAppendix });
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `${sanitizeFileName(story?.title)} - NextScenes Manuscript - ${stamp}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function escapeManuscriptHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function stripManuscriptEditMarkers(value) {
  return String(value || "")
    .replace(/\[\[EDITED\]\]/g, "")
    .replace(/\[\[\/EDITED\]\]/g, "");
}

function manuscriptMarkedRevisionToPlainText(value) {
  return String(value || "")
    .replace(/\[\[EDITED\]\]/g, "[changed] ")
    .replace(/\[\[\/EDITED\]\]/g, " [/changed]");
}

function manuscriptMarkedRevisionToHtml(value) {
  const raw = String(value || "");
  const parts = raw.split(/(\[\[EDITED\]\]|\[\[\/EDITED\]\])/g);
  let edited = false;
  const htmlParts = [];

  parts.forEach((part) => {
    if (part === "[[EDITED]]") {
      edited = true;
      return;
    }
    if (part === "[[/EDITED]]") {
      edited = false;
      return;
    }
    if (!part) return;

    const escaped = escapeManuscriptHtml(part).replace(/\n/g, "<br />");
    if (edited) {
      htmlParts.push(`<span style="color:#b91c1c;font-style:italic;background:#fee2e2;padding:0 3px;border-radius:5px;">${escaped}</span>`);
    } else {
      htmlParts.push(escaped);
    }
  });

  return `<div style="font-family:Georgia,'Times New Roman',serif;line-height:1.7;white-space:pre-wrap;color:#0f172a;">${htmlParts.join("")}</div>`;
}

async function copyManuscriptTextToClipboard({ plainText, htmlText }) {
  const plain = String(plainText || "");
  if (!plain) throw new Error("Nothing to copy.");

  if (typeof navigator !== "undefined" && navigator.clipboard) {
    if (htmlText && typeof window !== "undefined" && window.ClipboardItem) {
      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlText], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        return;
      } catch (_err) {
        // Some browsers block rich clipboard writes. Fall back to plain text.
      }
    }

    try {
      await navigator.clipboard.writeText(plain);
      return;
    } catch (_err) {
      // Fall back to the older textarea method below.
    }
  }

  if (typeof document === "undefined") throw new Error("Clipboard unavailable.");
  const textarea = document.createElement("textarea");
  textarea.value = plain;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const ok = document.execCommand("copy");
  textarea.remove();
  if (!ok) throw new Error("Clipboard copy failed.");
}

function buildManuscriptEditorialDraftStorageKey(storyId) {
  const id = safeText(storyId);
  return id ? `nextscenes:manuscript-editor:editorial-drafts:${id}` : "";
}

async function invokeTry(tries) {
  let lastErr = null;
  for (const fn of tries) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Action failed.");
}

export default function StoryDashboard({
  onNavigatePage,
  projectHubContext = {},
}) {
  const { isAuthenticated, user } = useUser();

  const sharedProjectContextPilot =
    sharedProjectContextPilotEnabled();

  const lang =
    (typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en";
  const t = TEXT[lang] || TEXT.en;
  const isFR = String(lang).toLowerCase() === "fr";

  const ui = useMemo(
    () => ({
      title: isFR ? "Story Dashboard" : "Story Dashboard",
      subtitle: isFR
        ? "Le poste de commandement de l’auteur. Ici, vous voyez toute l’histoire, le canon, les propositions, l’activité et les réglages, dans un ordre strict."
        : "The author’s command room. Here you see the whole story, the canon, the proposals, the activity, and the controls in a disciplined structure.",
      illustrationApprovalGuardrail:
        t.illustrationApprovalGuardrail ||
        (isFR
          ? "Approuvez seulement les illustrations adaptées, légales et liées à la scène."
          : "Approve only suitable, lawful, scene-related illustrations."),
      illustrationPolicyLink: t.sceneIllustrationPolicyLink || (isFR ? "Politique sur les illustrations" : "Illustration Upload Policy"),
      illustrationPolicyHref: isFR
        ? "https://www.nextscenes.org/fr/politique-illustrations-televersements"
        : "https://www.nextscenes.org/illustration-upload-policy",
      back: t.back || (isFR ? "Retour" : "Back"),
      authoredStories: isFR ? "Vos storylines" : "Your storylines",
      searchPlaceholder: t.searchPlaceholder || (isFR ? "Rechercher vos storylines…" : "Search your storylines…"),
      clear: t.clear || (isFR ? "Effacer" : "Clear"),
      noStories: isFR ? "Aucune storyline d’auteur trouvée." : "No authored storylines found.",
      noStoriesHint: isFR
        ? "Créez une storyline dans Story Hub, puis revenez ici pour la gouverner."
        : "Create a storyline in Story Hub, then come back here to govern it.",
      open: isFR ? "Ouvrir" : "Open",
      openInWriterStudio: isFR ? "Ouvrir dans Writer Studio" : "Open in Writer Studio",
      openInReaderView: isFR ? "Ouvrir dans Reader View" : "Open in Reader View",
      viewPublishedBook: isFR ? "Voir le livre publié" : "View Published Book",
      viewOnAmazon: isFR ? "Voir sur Amazon" : "View on Amazon",
      publishedBook: isFR ? "Livre publié" : "Published Book",
      previewOnNextScenes: isFR ? "Aperçu sur NextScenes" : "Preview on NextScenes",
      publishedDashboardNote: isFR ? "Cette œuvre est publiée ou terminée. Les outils de construction sont masqués ici pour éviter de traiter un livre fini comme un brouillon actif." : "This work is published or completed. Workshop tools are hidden here so a finished book is not treated like an active draft.",
      createStoryline: isFR ? "Créer une storyline" : "Create a storyline",
      dashboardWelcome: isFR ? "Bienvenue dans Story Dashboard" : "Welcome to Story Dashboard",
      dashboardIntro: isFR
        ? "Choisissez une storyline à gauche. Cette page vous montre toute l’histoire dans une vue structurée et disciplinée."
        : "Choose a storyline on the left. This page shows you the full story in a structured and disciplined view.",
      storyIdentity: isFR ? "Identité de la storyline" : "Story identity",
      canonTimeline: isFR ? "Chronologie canonique" : "Canon timeline",
      proposalChamber: isFR ? "Chambre des propositions" : "Proposal chamber",
      rejectedHistory: isFR ? "Historique des propositions de scène rejetées" : "Rejected scene proposal history",
      storyIntelligence: isFR ? "Intelligence de la storyline" : "Story intelligence",
      storyDetails: isFR ? "Détails de la storyline" : "Story details",
      titleLabel: t.title || (isFR ? "Titre" : "Title"),
      forum: t.forum || (isFR ? "Forum" : "Forum"),
      writingMode: t.writingModeLabel || (isFR ? "Mode d’écriture" : "Writing mode"),
      createdAt: t.dateTimeCreated || (isFR ? "Date et heure de création" : "Date & time created"),
      updatedAt: t.lastUpdated || (isFR ? "Dernière mise à jour" : "Last updated"),
      currentState: isFR ? "État actuel" : "Current state",
      canonState: isFR ? "Mode canon" : "Canon mode",
      proposalState: isFR ? "Mode propositions" : "Proposal mode",
      description: t.description || (isFR ? "Description" : "Description"),
      synopsis: t.synopsis || (isFR ? "Synopsis" : "Synopsis"),
      aboutAuthor: t.aboutTheAuthor || (isFR ? "À propos de l’auteur" : "About the author"),
      storyDetailsTitle: isFR ? "Détails de l’histoire" : "Story details",
      noDescriptionYet: t.noDescriptionYet || (isFR ? "Aucune description pour le moment." : "No description yet."),
      noSynopsisYet: t.noSynopsisYet || (isFR ? "Aucun synopsis pour le moment." : "No synopsis yet."),
      noAuthorBioYet: t.noAuthorBioYet || (isFR ? "Aucune biographie d’auteur pour le moment." : "No author bio yet."),
      soloWriting: t.soloWriting || (isFR ? "Écriture solo" : "Solo Writing"),
      collaborativeWriting: t.collaborativeWriting || (isFR ? "Écriture collaborative" : "Collaborative Writing"),
      groupWritingOpen: t.groupWritingOpen || (isFR ? "Écriture de groupe (ouverte)" : "Group Writing (Open)"),
      groupWritingClosed: t.groupWritingClosed || (isFR ? "Écriture de groupe (fermée)" : "Group Writing (Closed)"),
      canon: isFR ? "Canon" : "Canon",
      pending: t.pending || (isFR ? "En attente" : "Pending"),
      rejected: t.rejected || (isFR ? "Rejetée" : "Rejected"),
      unknown: t.unknown || (isFR ? "Inconnu" : "Unknown"),
      untitledScene: isFR ? "Scène sans titre" : "Untitled scene",
      scene: isFR ? "Scène" : "Scene",
      proposal: isFR ? "Proposition" : "Proposal",
      viewScene: isFR ? "Voir la scène" : "View scene",
      preview: isFR ? "Aperçu" : "Preview",
      by: t.by || (isFR ? "Par" : "By"),
      submitted: isFR ? "Soumise" : "Submitted",
      approvedOn: isFR ? "Approuvée le" : "Approved on",
      sceneNumber: isFR ? "Numéro de scène" : "Scene number",
      readerInterest: isFR ? "Intérêt lecteur" : "Reader interest",
      readerInterestWaiting: isFR ? "En attente" : "Waiting",
      readerInterestExcellent: isFR ? "Excellent" : "Excellent",
      readerInterestStrong: isFR ? "Fort" : "Strong",
      readerInterestSteady: isFR ? "Stable" : "Steady",
      readerInterestLow: isFR ? "Faible" : "Low",
      readerRatingSingular: isFR ? "{count} note" : "{count} rating",
      readerRatingPlural: isFR ? "{count} notes" : "{count} ratings",
      readerVerifiedShort: isFR ? "Vérifiées" : "Verified",
      readerGuestShort: isFR ? "Invités" : "Guest",
      readerMovement: isFR ? "Mouvement lecteur" : "Reader movement",
      readerMovementWaiting: isFR ? "En attente" : "Waiting",
      readerMovementStrong: isFR ? "Fort" : "Strong",
      readerMovementSteady: isFR ? "Stable" : "Steady",
      readerMovementEarly: isFR ? "Signal initial" : "Early signal",
      readerMovementWatch: isFR ? "À surveiller" : "Watch",
      readerMovementOpened: isFR ? "ouverts" : "opened",
      readerMovementCompleted: isFR ? "terminés" : "completed",
      readerMovementContinued: isFR ? "continués" : "continued",
      readerMovementReturned: isFR ? "revenus" : "returned",
      readerMovementReturn: isFR ? "Retour" : "Return signal",
      readerSignals: t.readerSignals || (isFR ? "Signaux lecteurs" : "Reader signals"),
      readerSignalsWaiting: t.readerSignalsWaiting || (isFR ? "En attente" : "Waiting"),
      readerSignalsSingular: t.readerSignalsSingular || (isFR ? "{count} signal" : "{count} signal"),
      readerSignalsPlural: t.readerSignalsPlural || (isFR ? "{count} signaux" : "{count} signals"),
      readerReactionLabels: t.sceneReactionLabels || {
        want_more: isFR ? "Je veux lire la suite" : "I want more",
        exciting: isFR ? "Passionnant" : "Exciting",
        emotional: isFR ? "Émouvant" : "Emotional",
        like_character: isFR ? "J’aime ce personnage" : "I like this character",
        confusing: isFR ? "Confus" : "Confusing",
        too_long: isFR ? "Trop long" : "Too long",
        afraid_for_character: isFR ? "J’ai peur pour ce personnage" : "I fear for this character",
        curious_ending: isFR ? "Fin intrigante" : "Curious ending",
      },
      proposalQueueEmpty: isFR ? "Aucune proposition en attente." : "No pending proposals.",
      rejectAll: isFR ? "Tout rejeter" : "Reject All",
      approve: isFR ? "Approuver" : "Approve",
      reject: isFR ? "Rejeter" : "Reject",
      sceneIllustration: isFR ? "Illustration de scène" : "Scene illustration",
      illustrationPending: isFR ? "Illustration en attente" : "Illustration pending",
      illustrationApproved: isFR ? "Illustration approuvée" : "Illustration approved",
      illustrationRejected: isFR ? "Illustration rejetée" : "Illustration rejected",
      illustrationCredit: isFR ? "Crédit" : "Credit",
      submittedBy: isFR ? "Soumise par" : "Submitted by",
      reviewedBy: isFR ? "Vérifiée par" : "Reviewed by",
      approveIllustration: isFR ? "Approuver l’illustration" : "Approve illustration",
      rejectIllustration: isFR ? "Rejeter l’illustration" : "Reject illustration",
      illustrationMissing: isFR ? "Aucune image" : "No image yet",
      illustrationMissingHelp: isFR
        ? "Ajoutez une illustration à cette scène enregistrée. Elle restera en attente jusqu’à approbation."
        : "Add an illustration to this saved scene. It will remain pending until approval.",
      addIllustrationImage: isFR ? "Ajouter une image" : "Add image",
      addIllustrationTitle: isFR ? "Ajouter une image à la scène" : "Add scene image",
      addIllustrationHelp: isFR
        ? "Joignez une image à cette scène. Elle restera en attente jusqu’à approbation."
        : "Attach an image to this scene. It will remain pending until approval.",
      addIllustrationConfirm: isFR ? "Ajouter l’image" : "Add image",
      changeIllustrationImage: isFR ? "Changer l’image" : "Change image",
      changeIllustrationTitle: isFR ? "Changer l’image de la scène" : "Change scene image",
      changeIllustrationHelp: isFR
        ? "Remplacez l’image attachée à cette scène. La nouvelle image restera en attente jusqu’à approbation."
        : "Replace the image attached to this scene. The new image remains pending until approval.",
      changeIllustrationFileLabel: isFR ? "Image" : "Image",
      changeIllustrationUrlLabel: isFR ? "Ou coller une URL d’image" : "Or paste an image URL",
      changeIllustrationCaptionLabel: isFR ? "Légende" : "Caption",
      changeIllustrationCreditLabel: isFR ? "Crédit" : "Credit",
      changeIllustrationAltLabel: isFR ? "Texte alternatif" : "Alt text",
      changeIllustrationConfirm: isFR ? "Remplacer l’image" : "Replace image",
      changeIllustrationMissing: isFR ? "Choisissez une image ou collez une URL." : "Choose an image or paste an image URL.",
      changeIllustrationSuccess: isFR ? "Image enregistrée. Vérifiez puis approuvez l’illustration." : "Image saved. Review and approve the illustration.",
      changeIllustrationFailed: isFR ? "L’enregistrement de l’image a échoué." : "Image save failed.",
      imageUploading: isFR ? "Téléversement…" : "Uploading…",
      confirmApproveIllustration: isFR ? "Approuver cette illustration pour affichage public ?" : "Approve this illustration for public display?",
      illustrationApproveTitle: isFR ? "Approuver l’illustration" : "Approve illustration",
      illustrationApproveHelp: isFR
        ? "Vérifiez l’image, la légende et le crédit avant de la rendre visible dans Reader View."
        : "Review the image, caption, and credit before making it visible in Reader View.",
      illustrationApproveNow: isFR ? "Approuver l’illustration" : "Approve illustration",
      illustrationPublicDisplay: isFR ? "Affichage public" : "Public display",
      illustrationRulesSummary: isFR ? "Règles" : "Rules",
      confirmRejectIllustration: isFR ? "Rejeter cette illustration ?" : "Reject this illustration?",
      illustrationRejectTitle: isFR ? "Rejeter l’illustration" : "Reject illustration",
      illustrationRejectHelp: isFR
        ? "Choisissez une raison claire. Le contributeur verra cette raison et pourra mieux proposer la prochaine fois."
        : "Choose a clear reason. The contributor will see this reason and can improve next time.",
      illustrationRejectReasonLabel: isFR ? "Raison du rejet" : "Rejection reason",
      illustrationRejectNoteLabel: isFR ? "Note au contributeur" : "Note to contributor",
      illustrationRejectNotePlaceholder: isFR ? "Exemple : Le dessin est bon, mais il ne correspond pas à l’ambiance de la scène." : "Example: The drawing is good, but it does not match the mood of the scene.",
      illustrationRejectNow: isFR ? "Rejeter l’illustration" : "Reject illustration",
      illustrationRejectReasonRequired: isFR ? "Une raison est requise pour rejeter une illustration." : "A reason is required to reject an illustration.",
      illustrationReviewReason: isFR ? "Raison" : "Reason",
      illustrationReviewNote: isFR ? "Note" : "Note",
      illustrationReasonDoesNotMatch: isFR ? "Ne correspond pas à la scène" : "Does not match the scene",
      illustrationReasonImageQuality: isFR ? "Qualité d’image insuffisante" : "Image quality is not suitable",
      illustrationReasonConsentCopyright: isFR ? "Problème de consentement ou de droit d’auteur" : "Consent or copyright concern",
      illustrationReasonSafetyPolicy: isFR ? "Problème de sécurité ou de politique" : "Safety or policy concern",
      illustrationReasonOther: isFR ? "Autre" : "Other",
      removeScene: isFR ? "Retirer du canon" : "Remove from canon",
      restoreScene: isFR ? "Restaurer la scène" : "Restore scene",
      removedCanonScenesTitle: isFR ? "Scènes retirées du canon" : "Removed canon scenes",
      removedCanonScenesHelp: isFR
        ? "Ces scènes restent privées et récupérables. Elles ne figurent pas dans Reader View ni dans le manuscrit canonique."
        : "These scenes remain private and recoverable. They do not appear in Reader View or the canon manuscript.",
      noRemovedCanonScenes: isFR ? "Aucune scène retirée du canon." : "No removed canon scenes.",
      confirmRestoreScene: isFR ? "Restaurer cette scène dans le canon ?" : "Restore this scene to canon?",
      sceneRestored: isFR ? "Scène restaurée dans le canon." : "Scene restored to canon.",
      editDetails: isFR ? "Modifier les détails" : "Edit details",
      saveDetails: isFR ? "Enregistrer les détails" : "Save details",
      cancel: t.cancel || (isFR ? "Annuler" : "Cancel"),
      totalCanonScenes: isFR ? "Total des scènes canoniques" : "Total canon scenes",
      activeProposals: isFR ? "Propositions actives" : "Active proposals",
      rejectedProposals: isFR ? "Propositions rejetées" : "Rejected proposals",
      readers: isFR ? "Lecteurs" : "Readers",
      opens: isFR ? "Ouvertures" : "Opens",
      lastActivity: isFR ? "Dernière activité" : "Last activity",
      contributors: isFR ? "Contributeurs" : "Contributors",
      contributionRecordsTitle: isFR ? "Registre des contributions" : "Contribution records",
      contributionRecordsBody: isFR
        ? "Les propositions acceptées sont enregistrées pour la transparence, le crédit et un éventuel partage futur des bénéfices, le cas échéant."
        : "Accepted proposals are recorded for transparency, credit, and possible future benefit-sharing where applicable.",
      generateManuscriptTitle: isFR ? "Générer le manuscrit" : "Generate Manuscript",
      generateManuscriptBody: isFR
        ? "Cet outil rassemble les scènes approuvées du Canon dans un manuscrit propre. Les propositions en attente, les propositions rejetées, les commentaires et les notes privées ne sont pas incluses."
        : "This tool gathers approved Canon scenes into a clean manuscript. Pending proposals, rejected proposals, comments, and private notes are not included.",
      manuscriptBeforeExportTitle: isFR ? "Avant l’exportation" : "Before you export",
      manuscriptBeforeExportBody: isFR
        ? "Ce manuscrit est généré uniquement à partir des scènes approuvées du Canon. Vérifiez l’ordre du Canon, les titres des scènes et les registres de contribution avant de l’envoyer hors de NextScenes."
        : "This manuscript is generated from approved Canon scenes only. Review your Canon order, scene titles, and contributor records before sending it outside NextScenes.",
      manuscriptBeforeExportInternal: isFR
        ? "Avec annexe des contributions : recommandé pour les archives internes et le suivi des contributions."
        : "With contributor appendix: recommended for internal records and contribution tracking.",
      manuscriptBeforeExportExternal: isFR
        ? "Sans annexe des contributions : recommandé pour les éditeurs, agents, maisons d’édition ou lecteurs externes."
        : "Without contributor appendix: recommended for editors, agents, publishers, or outside readers.",
      policyLinksTitle: isFR ? "Liens utiles" : "Useful policy links",
      policyLinksBody: isFR
        ? "Avant de préparer un manuscrit pour l’extérieur, relisez les droits des auteurs, la politique des contributeurs, le partage des bénéfices et les conditions simples."
        : "Before preparing a manuscript for outside use, review writer rights, contributor policy, benefit-sharing, and simple terms.",
      storyPulse: isFR ? "Pulse de l’histoire" : "Story pulse",
      currentDirection: isFR ? "Direction actuelle" : "Current direction",
      statusReady: isFR ? "Prêt" : "Ready",
      loading: t.loading || (isFR ? "Chargement…" : "Loading…"),
      saving: isFR ? "Enregistrement…" : "Saving…",
      working: t.working || (isFR ? "Traitement…" : "Working…"),
      loadMore: isFR ? "Afficher plus" : "Load more",
      selected: t.selected || (isFR ? "Sélectionné" : "Selected"),
      refresh: isFR ? "Actualiser" : "Refresh",
      noCanonScenes: isFR ? "Aucune scène canonique approuvée pour le moment." : "No approved canon scenes yet.",
      noRejected: isFR ? "Aucune proposition de scène rejetée enregistrée." : "No rejected scene proposals recorded.",
      actionFailed: isFR ? "Action échouée." : "Action failed.",
      detailsSaved: isFR ? "Détails enregistrés." : "Details saved.",
      loginRequired: t.loginToParticipate || (isFR ? "Connectez-vous pour accéder au tableau de bord." : "Login to access the dashboard."),
      storylistCount: isFR ? "storylines" : "storylines",
      openStorylineHint: isFR ? "Ouvrir cette storyline dans le tableau de bord" : "Open this storyline in the dashboard",
      modeSwitchCanon: isFR ? "Passer en mode canon" : "Switch to canon mode",
      modeSwitchProposal: isFR ? "Passer en mode propositions" : "Switch to proposal mode",
      authorAuthorityLine: isFR
        ? "Ici, l’auteur voit toute l’histoire et en assume la direction."
        : "Here, the author sees the whole story and carries responsibility for its direction.",
      noAuthorMatches: isFR
        ? "Nous n’avons trouvé aucune storyline clairement liée à votre compte actuel."
        : "We could not find any storyline clearly linked to your current account.",
      inspectScene: isFR ? "Inspecter la scène" : "Inspect scene",
      confirmRemoveScene: isFR
        ? "Retirer cette scène du canon ? La scène restera récupérable."
        : "Remove this scene from canon? The scene will remain recoverable.",
      sceneRemoved: isFR ? "Scène retirée du canon." : "Scene removed from canon.",
      confirmApprove: isFR ? "Approuver cette proposition et l’ajouter au canon ?" : "Approve this proposal and add it to canon?",
      approveDecisionNotePrompt: isFR
        ? "Ajoutez une courte note de décision pour expliquer pourquoi cette proposition entre dans le canon."
        : "Add a short decision note explaining why this proposal is entering canon.",
      approveDecisionNoteRequired: isFR
        ? "Une note de décision est requise pour approuver une proposition."
        : "A decision note is required to approve a proposal.",
      approveDecisionNoteTitle: isFR ? "Note d’approbation" : "Approval note",
      approveDecisionNoteHelp: isFR
        ? "Expliquez brièvement pourquoi cette proposition entre dans le canon. Cette note restera dans le registre de décision."
        : "Briefly explain why this proposal is entering canon. This note will remain in the decision record.",
      approveDecisionNotePlaceholder: isFR ? "Exemple : Cette scène fait avancer l’histoire clairement." : "Example: This scene moves the story forward clearly.",
      approveNow: isFR ? "Approuver maintenant" : "Approve now",
      proposalApproved: isFR ? "Proposition approuvée et ajoutée au canon." : "Proposal approved and added to canon.",
      proposalRejected: isFR ? "Proposition rejetée." : "Proposal rejected.",
      allProposalsRejected: isFR ? "Toutes les propositions en attente ont été rejetées." : "All pending proposals have been rejected.",
      confirmReject: isFR ? "Rejeter cette proposition ?" : "Reject this proposal?",
      confirmRejectAll: isFR ? "Rejeter toutes les propositions en attente pour cette storyline ?" : "Reject all pending proposals for this storyline?",
      confirmSwitchCanon: isFR ? "Revenir au mode canon pour cette storyline ?" : "Return this storyline to canon mode?",
      confirmSwitchProposal: isFR ? "Ouvrir le mode propositions pour cette storyline ?" : "Open proposal mode for this storyline?",
      fullScene: isFR ? "Texte complet de la scène" : "Full scene text",
      metadataEditor: isFR ? "Éditeur des détails de la storyline" : "Story details editor",
      saveErrorRoute: isFR
        ? "L’action a échoué. Vérifiez l’alignement de la route backend pour cette opération."
        : "The action failed. Check whether the backend route name for this operation matches this page.",
      authoredByYou: isFR ? "Auteur : vous" : "Authored by you",
      storyHubStart: t.storyHubStartNew || (isFR ? "Story Hub → Créer une storyline" : "Story Hub → Create a storyline"),
      canonSearchPlaceholder: isFR ? "Rechercher dans le canon…" : "Search within canon…",
      canonContributorFilter: isFR ? "Filtrer par contributeur" : "Filter by contributor",
      allContributors: isFR ? "Tous les contributeurs" : "All contributors",
      jumpToScene: isFR ? "Aller à la scène" : "Jump to scene",
      jump: isFR ? "Aller" : "Go",
      first: isFR ? "Première" : "First",
      previous: isFR ? "Précédente" : "Previous",
      next: isFR ? "Suivante" : "Next",
      last: isFR ? "Dernière" : "Last",
      showingScenesRange: isFR ? "Affichage des scènes" : "Showing scenes",
      ofCanonFiltered: isFR ? "sur le canon filtré" : "of filtered canon",
      ofCanonTotal: isFR ? "sur le canon total" : "of total canon",
      pageLabel: isFR ? "Page" : "Page",
      noCanonMatch: isFR ? "Aucune scène canonique ne correspond à ce filtre." : "No canon scenes match this filter.",
      sceneSearchHint: isFR ? "Titre, texte ou contributeur" : "Title, text, or contributor",
      clearCanonFilters: isFR ? "Effacer les filtres du canon" : "Clear canon filters",
      invalidSceneNumber: isFR ? "Numéro de scène invalide." : "Invalid scene number.",
      contributor: isFR ? "Contributeur" : "Contributor",
      generateDocx: isFR ? "Générer le DOCX" : "Generate DOCX",
      manuscriptReadyHelp: isFR
        ? "Crée un fichier DOCX à partir des scènes approuvées du Canon uniquement. L’annexe des contributions peut être incluse ou retirée selon l’usage du document."
        : "Creates a DOCX file from approved Canon scenes only. The contributor appendix can be included or removed depending on how the document will be used.",
      manuscriptExportOptions: isFR ? "Options d’exportation" : "Export options",
      includeContributorAppendix: isFR ? "Inclure l’annexe des contributions" : "Include contributor appendix",
      includeContributorAppendixHelp: isFR
        ? "Recommandé pour les archives internes de NextScenes et le suivi des contributions. Désactivez-le pour un manuscrit plus propre à envoyer à un éditeur, un agent ou un lecteur externe."
        : "Recommended for internal NextScenes records and contribution tracking. Turn it off for a cleaner manuscript to send to an editor, agent, or outside reader.",
      manuscriptNoCanon: isFR
        ? "Aucune scène canonique approuvée n’est disponible pour générer un manuscrit."
        : "No approved Canon scene is available for manuscript generation.",
      manuscriptDownloadStarted: isFR
        ? "Manuscrit généré avec succès. Vérifiez vos téléchargements."
        : "Manuscript generated successfully. Check your downloads.",
      manuscriptDownloadFailed: isFR
        ? "La génération du manuscrit a échoué."
        : "Manuscript generation failed.",
      manuscriptEditorTitle: isFR ? "Éditeur de manuscrit" : "Manuscript Editor",
      manuscriptEditorBody: isFR
        ? "Crée d’abord une carte de l’âme du livre et un registre de continuité à partir du manuscrit canonique. L’édition se fera ensuite chapitre par chapitre, avec l’autorité finale de l’auteur."
        : "First build a Book Soul Map and Continuity Ledger from the approved Canon manuscript. Editing will then happen chapter by chapter, with the author keeping final authority.",
      manuscriptEditorCreate: isFR ? "Créer le projet éditorial" : "Create Editorial Project",
      manuscriptEditorBusy: isFR ? "Lecture du manuscrit…" : "Reading manuscript…",
      manuscriptEditorProgressReading: isFR ? "Lecture du manuscrit…" : "Reading manuscript…",
      manuscriptEditorProgressSoul: isFR ? "Création de la carte de l’âme du livre…" : "Building Book Soul Map…",
      manuscriptEditorProgressContinuity: isFR ? "Vérification des fils de continuité…" : "Checking continuity threads…",
      manuscriptEditorProgressChapters: isFR ? "Préparation de la carte des chapitres…" : "Preparing chapter map…",
      manuscriptEditorRefresh: isFR ? "Rafraîchir le projet éditorial" : "Refresh Editorial Project",
      manuscriptEditorCached: isFR ? "Carte éditoriale sauvegardée chargée." : "Saved editorial map loaded.",
      manuscriptEditorStartPass: isFR ? "Commencer la passe éditoriale par chapitre" : "Start Chapter Editorial Pass",
      manuscriptEditorStartPassHelp: isFR
        ? "Prochaine étape : choisir un chapitre, sélectionner le type d’édition, puis recevoir une version marquée et une version propre."
        : "Next step: choose one chapter, select the editing mode, then receive a marked version and a clean version.",
      manuscriptEditorSuccess: isFR
        ? "Carte de l’âme du livre créée. Vérifiez-la avant toute édition."
        : "Book Soul Map created. Review it before any editing pass.",
      manuscriptEditorFailed: isFR
        ? "La création du projet éditorial a échoué."
        : "Editorial project creation failed.",
      manuscriptEditorNoCanon: isFR
        ? "Aucune scène canonique approuvée n’est disponible pour créer un projet éditorial."
        : "No approved Canon scene is available for an editorial project.",
      manuscriptEditorResultTitle: isFR ? "Carte de l’âme du livre" : "Book Soul Map",
      manuscriptEditorContinuityTitle: isFR ? "Registre de continuité" : "Continuity Ledger",
      manuscriptEditorChapterMapTitle: isFR ? "Carte des chapitres" : "Chapter Map",
      manuscriptEditorProtectedTitle: isFR ? "À protéger" : "Protect These",
      manuscriptEditorNextStepsTitle: isFR ? "Prochaines étapes" : "Next Steps",
      manuscriptEditorAdvisory: isFR
        ? "Lecture éditoriale consultative uniquement. L’auteur décide."
        : "Advisory editorial reading only. The author decides.",
      manuscriptEditorProviderBadge: isFR ? "Conseil IA seulement" : "AI advisory only",
      manuscriptEditorMappedChapters: isFR ? "chapitres cartographiés" : "chapters mapped",
      manuscriptChapterEditorTitle: isFR ? "Passe éditoriale du chapitre" : "Chapter Editorial Pass",
      manuscriptChapterEditorChooseChapter: isFR ? "Choisir un chapitre" : "Choose chapter",
      manuscriptChapterEditorMode: isFR ? "Mode d’édition" : "Editing mode",
      manuscriptChapterEditorRun: isFR ? "Lancer la passe éditoriale" : "Run Editorial Pass",
      manuscriptChapterEditorBusy: isFR ? "Édition du chapitre…" : "Editing chapter…",
      manuscriptChapterEditorOpen: isFR ? "Ouvrir la passe éditoriale" : "Open Editorial Pass",
      manuscriptChapterEditorClose: isFR ? "Fermer" : "Close",
      manuscriptChapterEditorResult: isFR ? "Résultat éditorial" : "Editorial Result",
      manuscriptChapterEditorMarked: isFR ? "Version marquée avec changements" : "Marked Revision with Changes",
      manuscriptChapterEditorClean: isFR ? "Version propre proposée" : "Clean Proposed Revision",
      manuscriptChapterEditorNotes: isFR ? "Notes éditoriales" : "Editorial Notes",
      manuscriptChapterEditorWarnings: isFR ? "Alertes de continuité" : "Continuity Warnings",
      manuscriptChapterEditorChanges: isFR ? "Résumé des changements" : "Change Summary",
      manuscriptChapterEditorNoMap: isFR ? "Créez d’abord la carte de l’âme du livre." : "Create the Book Soul Map first.",
      manuscriptChapterEditorFailed: isFR ? "La passe éditoriale du chapitre a échoué." : "Chapter editorial pass failed.",
      manuscriptChapterEditorSuccess: isFR ? "Passe éditoriale terminée. Vérifiez avant de décider." : "Editorial pass complete. Review before deciding.",
      manuscriptDecisionControlsTitle: isFR ? "Contrôles de décision de l’auteur" : "Author Decision Controls",
      manuscriptDecisionControlsBody: isFR
        ? "Copiez ou sauvegardez cette proposition séparément. Rien ici ne modifie le Canon."
        : "Copy or save this proposal separately. Nothing here changes Canon.",
      manuscriptCopyClean: isFR ? "Copier la version propre" : "Copy Clean Revision",
      manuscriptCopyMarked: isFR ? "Copier la version marquée" : "Copy Marked Revision",
      manuscriptSaveEditorialDraft: isFR ? "Sauvegarder comme brouillon éditorial" : "Save as Editorial Draft",
      manuscriptCopyCleanSuccess: isFR ? "Version propre copiée. Le Canon reste inchangé." : "Clean revision copied. Canon remains unchanged.",
      manuscriptCopyMarkedSuccess: isFR ? "Version marquée copiée. Le Canon reste inchangé." : "Marked revision copied. Canon remains unchanged.",
      manuscriptSaveDraftSuccess: isFR ? "Brouillon éditorial sauvegardé séparément. Le Canon reste inchangé." : "Editorial draft saved separately. Canon remains unchanged.",
      manuscriptCopyFailed: isFR ? "La copie a échoué. Sélectionnez le texte manuellement." : "Copy failed. Please select the text manually.",
      manuscriptSavedDraftsTitle: isFR ? "Brouillons éditoriaux sauvegardés" : "Saved Editorial Drafts",
      manuscriptSavedDraftsHelp: isFR
        ? "Ces brouillons restent séparés du Canon et sont gardés dans ce navigateur pour aider l’auteur à décider."
        : "These drafts stay separate from Canon and are kept in this browser to help the author decide.",
      manuscriptSavedDraftClean: isFR ? "Copier propre" : "Copy clean",
      manuscriptSavedDraftMarked: isFR ? "Copier marqué" : "Copy marked",
      manuscriptDraftLocalOnly: isFR ? "Sauvegarde locale séparée" : "Separate local save",
      manuscriptApplyCanonPostponed: isFR ? "Appliquer au Canon : reporté pour sécurité" : "Apply to Canon: postponed for safety",
      manuscriptEditModeLight: isFR ? "Polissage léger" : "Light Polish",
      manuscriptEditModeVoice: isFR ? "Voix de l’auteur" : "Author Voice Pass",
      manuscriptEditModeContinuity: isFR ? "Continuité" : "Continuity Pass",
      manuscriptEditModeDeep: isFR ? "Édition approfondie" : "Deep Editorial Pass",
      manuscriptEditorEmptyField: isFR ? "Non encore identifié." : "Not identified yet.",
    }),
    [isFR, t]
  );

  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingStory, setLoadingStory] = useState(false);
  const [workingAction, setWorkingAction] = useState("");
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(STORY_LIST_PAGE_SIZE);
  const [canonSearchInput, setCanonSearchInput] = useState("");
  const [canonSearchQuery, setCanonSearchQuery] = useState("");
  const [canonContributorFilter, setCanonContributorFilter] = useState("all");
  const [canonPage, setCanonPage] = useState(1);
  const [canonJumpInput, setCanonJumpInput] = useState("");
  const [canonJumpError, setCanonJumpError] = useState("");
  const [sceneModal, setSceneModal] = useState({ open: false, scene: null, kind: "canon", number: null });
  const [ownerSettingsOpen, setOwnerSettingsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", synopsis: "", aboutAuthor: "" });
  const [includeContributorAppendix, setIncludeContributorAppendix] = useState(true);
  const [manuscriptEditorBusy, setManuscriptEditorBusy] = useState(false);
  const [manuscriptEditorPhaseIndex, setManuscriptEditorPhaseIndex] = useState(0);
  const [manuscriptEditorResult, setManuscriptEditorResult] = useState(null);
  const [manuscriptChapterEditorOpen, setManuscriptChapterEditorOpen] = useState(false);
  const [manuscriptChapterEditorBusy, setManuscriptChapterEditorBusy] = useState(false);
  const [manuscriptChapterEditorIndex, setManuscriptChapterEditorIndex] = useState(0);
  const [manuscriptChapterEditorMode, setManuscriptChapterEditorMode] = useState("author_voice");
  const [manuscriptChapterEditorResult, setManuscriptChapterEditorResult] = useState(null);
  const [manuscriptEditorialDrafts, setManuscriptEditorialDrafts] = useState([]);
  const [sceneRatingSummaries, setSceneRatingSummaries] = useState({});
  const [sceneRatingsLoading, setSceneRatingsLoading] = useState(false);
  const [sceneReactionSummaries, setSceneReactionSummaries] = useState({});
  const [sceneReactionsLoading, setSceneReactionsLoading] = useState(false);
  const [sceneCompletionSummaries, setSceneCompletionSummaries] = useState({});
  const [sceneCompletionLoading, setSceneCompletionLoading] = useState(false);
  const [approveNoteModal, setApproveNoteModal] = useState({ open: false, scene: null, note: "" });
  const [approveIllustrationModal, setApproveIllustrationModal] = useState({ open: false, scene: null });
  const [rejectIllustrationModal, setRejectIllustrationModal] = useState({
    open: false,
    scene: null,
    reason: "does_not_match_scene",
    note: "",
  });

  const [changeIllustrationModal, setChangeIllustrationModal] = useState({
    open: false,
    scene: null,
    file: null,
    url: "",
    caption: "",
    credit: "",
    alt: "",
    uploading: false,
  });

  const mountedRef = useRef(true);
  const storyCacheRef = useRef(new Map());
  const storyReqSeqRef = useRef(0);
  const sceneRatingsReqSeqRef = useRef(0);
  const sceneReactionsReqSeqRef = useRef(0);
  const sceneCompletionReqSeqRef = useRef(0);
  const selectedItemRef = useRef(null);
  const listScrollRef = useRef(null);
  const canonScrollRef = useRef(null);
  const didAutoScrollRef = useRef(false);
  const initialTargetRef = useRef("");

  useEffect(() => {
    mountedRef.current = true;
    initialTargetRef.current = parseStoryIdFromUrl();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(safeText(searchInput));
    }, 140);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCanonSearchQuery(safeText(canonSearchInput));
    }, 140);
    return () => window.clearTimeout(timer);
  }, [canonSearchInput]);

  useEffect(() => {
    setVisibleCount(STORY_LIST_PAGE_SIZE);
  }, [searchQuery]);

  const selectedId = getId(selectedStory);

  const [sharedProjectContext, setSharedProjectContext] =
    useState(null);

  const [
    sharedProjectContextLoading,
    setSharedProjectContextLoading,
  ] = useState(false);

  const [
    sharedProjectContextError,
    setSharedProjectContextError,
  ] = useState("");

  function putStoryInCache(storyLike, isFull = false) {
    const key = buildStoryCacheKey(storyLike);
    if (!key) return;
    const current = storyCacheRef.current.get(key) || {};
    const next = { ...current, ...(storyLike || {}) };
    if (isFull) next.__full = true;
    storyCacheRef.current.set(key, next);

    getIdAliases(storyLike).forEach((alias) => {
      if (alias && alias !== key) storyCacheRef.current.set(alias, next);
    });

    if (storyCacheRef.current.size > STORY_CACHE_LIMIT) {
      const firstKey = storyCacheRef.current.keys().next().value;
      if (firstKey) storyCacheRef.current.delete(firstKey);
    }
  }

  function getStoryFromCache(id) {
    const key = safeText(id);
    if (!key) return null;
    return storyCacheRef.current.get(key) || null;
  }

  async function fetchStoriesList() {
    const list =
      typeof api.getStoriesList === "function"
        ? await api.getStoriesList({ limit: DASHBOARD_LIST_LIMIT })
        : await api("/api/stories?mode=list");
    return normalizeStoriesListResponse(list);
  }

  async function openStory(id, opts = {}) {
    const cleanId = safeText(id);
    if (!cleanId) return;
    const requestSeq = storyReqSeqRef.current + 1;
    storyReqSeqRef.current = requestSeq;

    const cached = getStoryFromCache(cleanId);
    if (cached && cached.__full && !opts.force) {
      setSelectedStory(cached);
      setStoryIdInUrl(cleanId);
      return;
    }

    try {
      setLoadingStory(true);
      setActionError("");
      setActionSuccess("");

      const full =
        typeof api.getStoryById === "function"
          ? await api.getStoryById(cleanId)
          : await api(`/api/stories/${cleanId}`);

      if (!mountedRef.current || requestSeq !== storyReqSeqRef.current) return;
      putStoryInCache(full, true);
      setSelectedStory(full || null);
      setStoryIdInUrl(getId(full) || cleanId);
    } catch (e) {
      if (!mountedRef.current || requestSeq !== storyReqSeqRef.current) return;
      setActionError(safeText(e?.message) || ui.actionFailed);
    } finally {
      if (mountedRef.current && requestSeq === storyReqSeqRef.current) setLoadingStory(false);
    }
  }

  async function reloadSelectedStory() {
    if (!selectedId) return;
    await openStory(selectedId, { force: true });
  }

  useEffect(() => {
    let active = true;

    async function loadSharedProjectContext() {
      if (
        !sharedProjectContextPilot ||
        !isAuthenticated ||
        !selectedId
      ) {
        if (active) {
          setSharedProjectContext(null);
          setSharedProjectContextError("");
          setSharedProjectContextLoading(false);
        }
        return;
      }

      try {
        setSharedProjectContextLoading(true);
        setSharedProjectContextError("");

        const payload =
          typeof api.getSharedProjectContext === "function"
            ? await api.getSharedProjectContext(selectedId)
            : await api(
                `/api/stories/${encodeURIComponent(selectedId)}/project-context`,
                { method: "GET" }
              );

        if (!active) return;

        setSharedProjectContext(payload?.context || null);
      } catch (error) {
        if (!active) return;

        setSharedProjectContext(null);
        setSharedProjectContextError(
          safeText(error?.message) ||
            (isFR
              ? "Impossible de charger le contexte du projet."
              : "Could not load project context.")
        );
      } finally {
        if (active) {
          setSharedProjectContextLoading(false);
        }
      }
    }

    loadSharedProjectContext();

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    isFR,
    selectedId,
    sharedProjectContextPilot,
  ]);

  useEffect(() => {
    (async () => {
      if (!isAuthenticated) {
        setError(ui.loginRequired);
        setStories([]);
        return;
      }

      try {
        setLoadingList(true);
        setError("");

        const arr = await fetchStoriesList();
        if (!mountedRef.current) return;

        arr.forEach((story) => putStoryInCache(story, false));
        setStories(arr);

        const preferred = initialTargetRef.current;
        if (preferred) {
          const found = arr.find((s) => getIdAliases(s).includes(preferred));
          if (found) {
            await openStory(getId(found));
          } else {
            await openStory(preferred);
          }
        }
      } catch (e) {
        if (!mountedRef.current) return;
        setError(safeText(e?.message) || ui.actionFailed);
        setStories([]);
      } finally {
        if (mountedRef.current) setLoadingList(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const authoredStories = useMemo(() => {
    return safeArray(stories).filter((story) => isOwnedByCurrentUser(story, user));
  }, [stories, user]);

  const filteredStories = useMemo(() => {
    return authoredStories.filter((story) => storyMatchesQuery(story, searchQuery));
  }, [authoredStories, searchQuery]);

  const displayedStories = useMemo(() => {
    return filteredStories.slice(0, visibleCount);
  }, [filteredStories, visibleCount]);

  const hasMoreStories = displayedStories.length < filteredStories.length;

  useEffect(() => {
    didAutoScrollRef.current = false;
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || loadingList) return;
    const container = listScrollRef.current;
    const el = selectedItemRef.current;
    if (!container || !el || didAutoScrollRef.current) return;
    didAutoScrollRef.current = true;

    const timer = window.setTimeout(() => {
      try {
        const cTop = container.scrollTop;
        const cBottom = cTop + container.clientHeight;
        const eTop = el.offsetTop;
        const eBottom = eTop + el.offsetHeight;
        const pad = 12;
        if (eTop < cTop + pad || eBottom > cBottom - pad) {
          container.scrollTo({
            top: Math.max(0, eTop - Math.floor(container.clientHeight * 0.25)),
            behavior: "smooth",
          });
        }
      } catch {}
    }, 70);

    return () => window.clearTimeout(timer);
  }, [selectedId, loadingList]);

  const canonScenes = useMemo(() => approvedCanonScenes(selectedStory), [selectedStory]);
  const removedCanon = useMemo(() => removedCanonScenes(selectedStory), [selectedStory]);
  const pendingProposals = useMemo(() => pendingProposalScenes(selectedStory), [selectedStory]);
  const rejectedProposals = useMemo(() => rejectedProposalScenes(selectedStory), [selectedStory]);

  const canonContributors = useMemo(() => {
    const names = canonScenes.map((scene) => safeSceneContributor(scene, ui)).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [canonScenes, ui]);

  const canonSceneNumberMap = useMemo(() => {
    const map = new Map();
    canonScenes.forEach((scene, index) => {
      const id = safeText(scene?._id || scene?.id);
      if (id) map.set(id, index + 1);
    });
    return map;
  }, [canonScenes]);

  useEffect(() => {
    const storyId = safeText(selectedId);
    const seq = sceneRatingsReqSeqRef.current + 1;
    sceneRatingsReqSeqRef.current = seq;

    if (!storyId || canonScenes.length === 0) {
      setSceneRatingSummaries({});
      setSceneRatingsLoading(false);
      return;
    }

    setSceneRatingsLoading(true);

    (async () => {
      try {
        const payload = await api(`/api/stories/${encodeURIComponent(storyId)}/scene-rating-summaries`);
        if (!mountedRef.current || sceneRatingsReqSeqRef.current !== seq) return;
        setSceneRatingSummaries(normalizeSceneRatingSummaryMap(payload));
      } catch {
        if (!mountedRef.current || sceneRatingsReqSeqRef.current !== seq) return;
        setSceneRatingSummaries({});
      } finally {
        if (mountedRef.current && sceneRatingsReqSeqRef.current === seq) setSceneRatingsLoading(false);
      }
    })();
  }, [selectedId, canonScenes.length]);

  useEffect(() => {
    const storyId = safeText(selectedId);
    const seq = sceneReactionsReqSeqRef.current + 1;
    sceneReactionsReqSeqRef.current = seq;

    if (!storyId || canonScenes.length === 0) {
      setSceneReactionSummaries({});
      setSceneReactionsLoading(false);
      return;
    }

    setSceneReactionsLoading(true);

    (async () => {
      try {
        const payload = await api(`/api/stories/${encodeURIComponent(storyId)}/scene-reaction-summaries`);
        if (!mountedRef.current || sceneReactionsReqSeqRef.current !== seq) return;
        setSceneReactionSummaries(normalizeSceneReactionSummaryMap(payload));
      } catch {
        if (!mountedRef.current || sceneReactionsReqSeqRef.current !== seq) return;
        setSceneReactionSummaries({});
      } finally {
        if (mountedRef.current && sceneReactionsReqSeqRef.current === seq) setSceneReactionsLoading(false);
      }
    })();
  }, [selectedId, canonScenes.length]);


  useEffect(() => {
    const storyId = safeText(selectedId);
    const seq = sceneCompletionReqSeqRef.current + 1;
    sceneCompletionReqSeqRef.current = seq;

    if (!storyId || canonScenes.length === 0) {
      setSceneCompletionSummaries({});
      setSceneCompletionLoading(false);
      return;
    }

    setSceneCompletionLoading(true);

    (async () => {
      try {
        const payload = await api(`/api/tracking/story/${encodeURIComponent(storyId)}/scene-progress-summaries`);
        if (!mountedRef.current || sceneCompletionReqSeqRef.current !== seq) return;
        setSceneCompletionSummaries(normalizeSceneCompletionSummaryMap(payload));
      } catch {
        if (!mountedRef.current || sceneCompletionReqSeqRef.current !== seq) return;
        setSceneCompletionSummaries({});
      } finally {
        if (mountedRef.current && sceneCompletionReqSeqRef.current === seq) setSceneCompletionLoading(false);
      }
    })();
  }, [selectedId, canonScenes.length]);

  const filteredCanonScenes = useMemo(() => {
    const q = normalizeForSearch(canonSearchQuery);
    return canonScenes.filter((scene) => {
      const contributor = safeSceneContributor(scene, ui);
      if (canonContributorFilter !== "all" && contributor !== canonContributorFilter) return false;
      if (!q) return true;
      const hay = [safeSceneTitle(scene, ui), safeSceneText(scene), contributor].map(normalizeForSearch).join(" ");
      return hay.includes(q);
    });
  }, [canonScenes, canonSearchQuery, canonContributorFilter, ui]);

  const canonPageCount = useMemo(() => {
    return Math.max(1, Math.ceil(filteredCanonScenes.length / CANON_PAGE_SIZE));
  }, [filteredCanonScenes]);

  const pagedCanonScenes = useMemo(() => {
    const start = (canonPage - 1) * CANON_PAGE_SIZE;
    return filteredCanonScenes.slice(start, start + CANON_PAGE_SIZE);
  }, [filteredCanonScenes, canonPage]);

  const canonRangeStart = filteredCanonScenes.length ? (canonPage - 1) * CANON_PAGE_SIZE + 1 : 0;
  const canonRangeEnd = filteredCanonScenes.length ? Math.min(canonPage * CANON_PAGE_SIZE, filteredCanonScenes.length) : 0;

  const selectedStructured = useMemo(() => {
    if (!selectedStory) return null;
    const createdAt = selectedStory?.createdAt || "";
    const updatedAt = selectedStory?.updatedAt || selectedStory?.lastUpdatedAt || selectedStory?.canonUpdatedAt || "";
    const lastActivityAt = storyLastActivityAt(selectedStory) || updatedAt || createdAt;
    return {
      title: safeText(selectedStory?.title) || "Untitled Storyline",
      forum: safeText(selectedStory?.forum) || (isFR ? "Forum non classé" : "Unclassified Forum"),
      writingMode: safeText(selectedStory?.writingMode) || "collaborative",
      proposalState: normalizeProposalState(selectedStory),
      description: guessDescription(selectedStory),
      synopsis: safeText(selectedStory?.synopsis || selectedStory?.summary || ""),
      aboutAuthor: safeText(selectedStory?.aboutAuthor || selectedStory?.authorBio || selectedStory?.bio || ""),
      createdAt,
      updatedAt,
      lastActivityAt,
      contributors: countStoryContributors(selectedStory, ui),
      pulseLabel: storyPulseLabel(selectedStory, lang),
      directionLabel: storyDirectionLabel(selectedStory, lang),
    };
  }, [selectedStory, isFR, lang, ui]);

  const selectedPublicationInfo = useMemo(() => {
    return selectedStory ? getPublicationInfo(selectedStory, lang) : null;
  }, [selectedStory, lang]);

  const selectedIsPublishedBook = Boolean(selectedPublicationInfo?.isPublished);

  const manuscriptEditorCacheKey = useMemo(() => {
    if (!selectedId) return "";
    const storyStamp = safeText(selectedStructured?.updatedAt || selectedStructured?.lastActivityAt || selectedStructured?.createdAt || "no-date");
    return `nextscenes:manuscript-editor:soul-map:${selectedId}:${canonScenes.length}:${storyStamp}`;
  }, [selectedId, canonScenes.length, selectedStructured?.updatedAt, selectedStructured?.lastActivityAt, selectedStructured?.createdAt]);

  const manuscriptEditorialDraftsKey = useMemo(() => buildManuscriptEditorialDraftStorageKey(selectedId), [selectedId]);

  const manuscriptEditorProgressSteps = useMemo(() => [
    ui.manuscriptEditorProgressReading,
    ui.manuscriptEditorProgressSoul,
    ui.manuscriptEditorProgressContinuity,
    ui.manuscriptEditorProgressChapters,
  ], [ui]);

  useEffect(() => {
    setManuscriptEditorBusy(false);
    setManuscriptEditorPhaseIndex(0);
    setManuscriptEditorResult(null);
    setManuscriptChapterEditorOpen(false);
    setManuscriptChapterEditorResult(null);
    setManuscriptChapterEditorBusy(false);
    setManuscriptChapterEditorIndex(0);

    if (!manuscriptEditorCacheKey || typeof window === "undefined") return;
    try {
      const cached = window.localStorage.getItem(manuscriptEditorCacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.soulMap) setManuscriptEditorResult(parsed);
      }
    } catch (_err) {
      // Ignore damaged browser cache. The writer can regenerate the map.
    }
  }, [manuscriptEditorCacheKey]);

  useEffect(() => {
    setManuscriptEditorialDrafts([]);
    if (!manuscriptEditorialDraftsKey || typeof window === "undefined") return;
    try {
      const saved = window.localStorage.getItem(manuscriptEditorialDraftsKey);
      const parsed = saved ? JSON.parse(saved) : [];
      setManuscriptEditorialDrafts(Array.isArray(parsed) ? parsed.slice(0, 12) : []);
    } catch (_err) {
      setManuscriptEditorialDrafts([]);
    }
  }, [manuscriptEditorialDraftsKey]);

  useEffect(() => {
    if (!manuscriptEditorBusy) return undefined;
    setManuscriptEditorPhaseIndex(0);
    const timer = window.setInterval(() => {
      setManuscriptEditorPhaseIndex((value) => (value + 1) % manuscriptEditorProgressSteps.length);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [manuscriptEditorBusy, manuscriptEditorProgressSteps.length]);

  useEffect(() => {
    setCanonPage(1);
    setCanonJumpInput("");
    setCanonJumpError("");
  }, [selectedId, canonSearchQuery, canonContributorFilter]);

  useEffect(() => {
    if (canonPage > canonPageCount) setCanonPage(canonPageCount);
  }, [canonPage, canonPageCount]);

  function clearCanonControls() {
    setCanonSearchInput("");
    setCanonSearchQuery("");
    setCanonContributorFilter("all");
    setCanonJumpInput("");
    setCanonJumpError("");
    setCanonPage(1);
  }

  function jumpToCanonScene() {
    const raw = safeText(canonJumpInput);
    const target = Number(raw);
    if (!Number.isInteger(target) || target < 1 || target > canonScenes.length) {
      setCanonJumpError(ui.invalidSceneNumber);
      return;
    }
    const scene = canonScenes[target - 1];
    const contributor = safeSceneContributor(scene, ui);
    const q = normalizeForSearch(canonSearchQuery);
    const hay = [safeSceneTitle(scene, ui), safeSceneText(scene), contributor].map(normalizeForSearch).join(" ");
    if ((canonContributorFilter !== "all" && contributor !== canonContributorFilter) || (q && !hay.includes(q))) {
      setCanonContributorFilter("all");
      setCanonSearchInput("");
      setCanonSearchQuery("");
    }
    setCanonJumpError("");
    setCanonPage(Math.floor((target - 1) / CANON_PAGE_SIZE) + 1);
  }

  async function handleGenerateManuscriptDocx() {
    if (!selectedStory || canonScenes.length === 0) {
      setActionSuccess("");
      setActionError(ui.manuscriptNoCanon);
      return;
    }

    try {
      setActionError("");
      await downloadStoryManuscriptDocx({ story: selectedStory, canonScenes, ui, lang, includeContributorAppendix });
      setActionSuccess(ui.manuscriptDownloadStarted);
    } catch (e) {
      setActionSuccess("");
      setActionError(safeText(e?.message) || ui.manuscriptDownloadFailed);
    }
  }

  async function handleCreateManuscriptEditorialProject() {
    if (manuscriptEditorBusy) return;
    if (!selectedStory || !selectedId || canonScenes.length === 0) {
      setActionSuccess("");
      setActionError(ui.manuscriptEditorNoCanon);
      return;
    }

    try {
      setActionError("");
      setActionSuccess("");
      setManuscriptEditorBusy(true);
      setManuscriptEditorPhaseIndex(0);
      setManuscriptEditorResult(null);

      const payload = await api.post("/api/ai/manuscript/soul-map", {
        storyId: selectedId,
        language: lang,
      });

      setManuscriptEditorResult(payload || null);
      if (payload?.soulMap && manuscriptEditorCacheKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(manuscriptEditorCacheKey, JSON.stringify(payload));
        } catch (_err) {
          // Browser storage may be full or unavailable. The result remains visible for this session.
        }
      }
      setActionSuccess(ui.manuscriptEditorSuccess);
    } catch (e) {
      setManuscriptEditorResult(null);
      setActionSuccess("");
      setActionError(safeText(e?.message) || ui.manuscriptEditorFailed);
    } finally {
      setManuscriptEditorBusy(false);
      setManuscriptEditorPhaseIndex(0);
    }
  }



  function renderManuscriptMarkedText(value) {
    const raw = String(value || "");
    const parts = raw.split(/(\[\[EDITED\]\]|\[\[\/EDITED\]\])/g);
    const output = [];
    let edited = false;

    parts.forEach((part, idx) => {
      if (part === "[[EDITED]]") {
        edited = true;
        return;
      }
      if (part === "[[/EDITED]]") {
        edited = false;
        return;
      }
      if (!part) return;

      output.push(
        edited ? (
          <span className="ns-manuscript-edited-segment" key={`edited-segment-${idx}`}>
            {part}
          </span>
        ) : (
          part
        )
      );
    });

    return output;
  }

  function openManuscriptChapterEditorialPass() {
    if (!manuscriptEditorResult?.soulMap) {
      setActionSuccess("");
      setActionError(ui.manuscriptChapterEditorNoMap);
      return;
    }
    setManuscriptChapterEditorOpen(true);
    setManuscriptChapterEditorResult(null);
  }

  async function handleRunManuscriptChapterEditorialPass() {
    if (manuscriptChapterEditorBusy) return;
    if (!selectedStory || !selectedId || !manuscriptEditorResult?.soulMap) {
      setActionSuccess("");
      setActionError(ui.manuscriptChapterEditorNoMap);
      return;
    }

    try {
      setActionError("");
      setActionSuccess("");
      setManuscriptChapterEditorBusy(true);
      setManuscriptChapterEditorResult(null);

      const payload = await api.post("/api/ai/manuscript/edit-chapter", {
        storyId: selectedId,
        language: lang,
        chapterIndex: Number(manuscriptChapterEditorIndex) || 0,
        editMode: manuscriptChapterEditorMode,
        soulMap: manuscriptEditorResult.soulMap || {},
        continuityLedger: manuscriptEditorResult.continuityLedger || {},
        chapterMap: manuscriptEditorResult.chapterMap || []
      });

      setManuscriptChapterEditorResult(payload || null);
      setActionSuccess(ui.manuscriptChapterEditorSuccess);
    } catch (e) {
      setActionSuccess("");
      setActionError(safeText(e?.message) || ui.manuscriptChapterEditorFailed);
    } finally {
      setManuscriptChapterEditorBusy(false);
    }
  }

  async function handleCopyManuscriptChapterRevision(kind, sourceDraft = null) {
    const source = sourceDraft || manuscriptChapterEditorResult || {};
    try {
      setActionError("");
      const isMarked = kind === "marked";
      const raw = isMarked ? source.markedRevision : source.cleanRevision;
      const plainText = isMarked ? manuscriptMarkedRevisionToPlainText(raw) : stripManuscriptEditMarkers(raw);
      const htmlText = isMarked ? manuscriptMarkedRevisionToHtml(raw) : null;
      await copyManuscriptTextToClipboard({ plainText, htmlText });
      setActionSuccess(isMarked ? ui.manuscriptCopyMarkedSuccess : ui.manuscriptCopyCleanSuccess);
    } catch (_err) {
      setActionSuccess("");
      setActionError(ui.manuscriptCopyFailed);
    }
  }

  function handleSaveManuscriptEditorialDraft() {
    if (!manuscriptChapterEditorResult || !manuscriptEditorialDraftsKey || typeof window === "undefined") return;

    const selectedChapter = safeArray(manuscriptEditorResult?.chapterMap)[Number(manuscriptChapterEditorIndex) || 0] || {};
    const draft = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: new Date().toISOString(),
      storyId: selectedId,
      storyTitle: safeText(selectedStructured?.title || selectedStory?.title),
      chapterIndex: Number(manuscriptChapterEditorIndex) || 0,
      chapterNumber: manuscriptChapterEditorResult.chapterNumber || Number(manuscriptChapterEditorIndex) + 1,
      chapterTitle: safeText(manuscriptChapterEditorResult.chapterTitle || selectedChapter.title || `${ui.scene} ${Number(manuscriptChapterEditorIndex) + 1}`),
      editMode: manuscriptChapterEditorResult.editMode || manuscriptChapterEditorMode,
      editModeLabel: manuscriptChapterEditorResult.editModeLabel || manuscriptChapterEditorMode,
      cleanRevision: String(manuscriptChapterEditorResult.cleanRevision || ""),
      markedRevision: String(manuscriptChapterEditorResult.markedRevision || ""),
      editorialNotes: safeArray(manuscriptChapterEditorResult.editorialNotes).slice(0, 12),
      continuityWarnings: safeArray(manuscriptChapterEditorResult.continuityWarnings).slice(0, 12),
      changeSummary: safeArray(manuscriptChapterEditorResult.changeSummary).slice(0, 16),
      advisoryOnly: true,
      mutatesStory: false,
    };

    const nextDrafts = [draft, ...manuscriptEditorialDrafts].slice(0, 12);
    setManuscriptEditorialDrafts(nextDrafts);
    try {
      window.localStorage.setItem(manuscriptEditorialDraftsKey, JSON.stringify(nextDrafts));
    } catch (_err) {
      // The current session still keeps the draft visible even if browser storage is full.
    }
    setActionError("");
    setActionSuccess(ui.manuscriptSaveDraftSuccess);
  }

  function openSceneModal(scene, kind, number = null) {
    setSceneModal({ open: true, scene, kind, number });
  }

  function closeSceneModal() {
    setSceneModal({ open: false, scene: null, kind: "canon", number: null });
  }

  function openEditModal() {
    if (!selectedStructured) return;
    setEditForm({
      title: selectedStructured.title,
      description: selectedStructured.description,
      synopsis: selectedStructured.synopsis,
      aboutAuthor: selectedStructured.aboutAuthor,
    });
    setEditOpen(true);
  }

  function closeEditModal() {
    setEditOpen(false);
  }

  async function saveStoryDetails() {
    if (!selectedId) return;
    try {
      setWorkingAction("save-details");
      setActionError("");
      setActionSuccess("");

      const payload = {
        title: safeText(editForm.title),
        description: safeText(editForm.description),
        synopsis: safeText(editForm.synopsis),
        aboutAuthor: safeText(editForm.aboutAuthor),
      };

      await invokeTry([
        () => api(`/api/stories/${encodeURIComponent(selectedId)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/update`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      await reloadSelectedStory();
      if (!mountedRef.current) return;
      setActionSuccess(ui.detailsSaved);
      setEditOpen(false);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  async function switchProposalMode(nextState) {
    if (!selectedId) return;
    const enteringProposal = nextState === "proposal";
    const confirmed = window.confirm(enteringProposal ? ui.confirmSwitchProposal : ui.confirmSwitchCanon);
    if (!confirmed) return;

    try {
      setWorkingAction(enteringProposal ? "proposal-mode" : "canon-mode");
      setActionError("");
      setActionSuccess("");

      await invokeTry([
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/proposal-mode`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ proposalMode: enteringProposal }),
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/mode`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state: nextState }),
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ proposalMode: enteringProposal, storyState: nextState }),
        }),
      ]);

      await reloadSelectedStory();
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  function approveProposal(scene) {
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;

    const fallbackTitle = safeText(scene?.title || scene?.sceneTitle || scene?.proposalTitle || "");
    const defaultNote = fallbackTitle
      ? `Approved into canon: ${fallbackTitle}`
      : "Approved into canon from Story Dashboard.";

    setActionError("");
    setActionSuccess("");
    setApproveNoteModal({ open: true, scene, note: defaultNote });
  }

  function closeApproveNoteModal() {
    setApproveNoteModal({ open: false, scene: null, note: "" });
  }

  async function confirmApproveProposal() {
    const scene = approveNoteModal.scene;
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;

    const decisionNote = safeText(approveNoteModal.note);
    if (!decisionNote) {
      setActionError(ui.approveDecisionNoteRequired);
      return;
    }

    try {
      setWorkingAction(`approve-${sceneId}`);
      setActionError("");
      setActionSuccess("");

      const res = await api(`/api/stories/${encodeURIComponent(selectedId)}/proposals`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "approve",
          proposalId: sceneId,
          sceneId,
          id: sceneId,
          decisionNote,
        }),
      });

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) {
        putStoryInCache(updated, true);
        setSelectedStory(updated);
      } else {
        await reloadSelectedStory();
      }

      if (!mountedRef.current) return;
      setActionSuccess(ui.proposalApproved);
      closeApproveNoteModal();
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  async function setSceneIllustrationStatus(scene, nextAction, extraPayload = {}) {
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;
    const isApprove = nextAction === "approve";
    const isClear = nextAction === "clear";

    try {
      setWorkingAction(`${nextAction}-illustration-${sceneId}`);
      setActionError("");
      setActionSuccess("");

      await api(`/api/stories/${encodeURIComponent(selectedId)}/scenes/${encodeURIComponent(sceneId)}/illustration`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: nextAction, ...extraPayload }),
      });

      await reloadSelectedStory();
      if (!mountedRef.current) return;
      setActionSuccess(isApprove ? ui.illustrationApproved : isClear ? ui.illustrationCleared : ui.illustrationRejected);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  function approveSceneIllustration(scene) {
    setActionError("");
    setActionSuccess("");
    setApproveIllustrationModal({ open: true, scene });
  }

  function closeApproveIllustrationModal() {
    setApproveIllustrationModal({ open: false, scene: null });
  }

  async function confirmApproveSceneIllustration() {
    const scene = approveIllustrationModal.scene;
    await setSceneIllustrationStatus(scene, "approve");
    if (mountedRef.current) closeApproveIllustrationModal();
  }

  function rejectSceneIllustration(scene) {
    setActionError("");
    setActionSuccess("");
    setRejectIllustrationModal({
      open: true,
      scene,
      reason: "does_not_match_scene",
      note: "",
    });
  }

  function closeRejectIllustrationModal() {
    setRejectIllustrationModal({ open: false, scene: null, reason: "does_not_match_scene", note: "" });
  }

  async function confirmRejectSceneIllustration() {
    const scene = rejectIllustrationModal.scene;
    const reason = safeText(rejectIllustrationModal.reason);
    if (!reason) {
      setActionError(ui.illustrationRejectReasonRequired);
      return;
    }

    await setSceneIllustrationStatus(scene, "reject", {
      illustrationReviewReason: reason,
      illustrationReviewNote: safeText(rejectIllustrationModal.note),
    });

    if (mountedRef.current) closeRejectIllustrationModal();
  }

  function openChangeIllustrationModal(scene) {
    const art = safeSceneIllustration(scene);
    setActionError("");
    setActionSuccess("");
    setApproveIllustrationModal({ open: false, scene: null });
    setRejectIllustrationModal({ open: false, scene: null, reason: "does_not_match_scene", note: "" });
    setSceneModal({ open: false, scene: null, kind: "canon", number: null });
    setChangeIllustrationModal({
      open: true,
      scene,
      file: null,
      url: "",
      caption: safeText(art?.caption),
      credit: safeText(art?.credit),
      alt: safeText(art?.alt),
      uploading: false,
    });
  }

  function closeChangeIllustrationModal() {
    setChangeIllustrationModal({
      open: false,
      scene: null,
      file: null,
      url: "",
      caption: "",
      credit: "",
      alt: "",
      uploading: false,
    });
  }

  async function confirmChangeSceneIllustration() {
    const scene = changeIllustrationModal.scene;
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;

    const pastedUrl = safeText(changeIllustrationModal.url);
    const file = changeIllustrationModal.file;
    if (!file && !pastedUrl) {
      setActionError(ui.changeIllustrationMissing);
      return;
    }

    try {
      setWorkingAction(`replace-illustration-${sceneId}`);
      setChangeIllustrationModal((current) => ({ ...current, uploading: Boolean(file) }));
      setActionError("");
      setActionSuccess("");

      let illustrationUrl = pastedUrl;
      if (file) {
        if (typeof api.uploadSceneIllustration !== "function") {
          throw new Error("Scene illustration upload is not available.");
        }
        const uploaded = await api.uploadSceneIllustration(file);
        illustrationUrl = safeText(uploaded?.url || uploaded?.publicUrl || uploaded?.imageUrl);
      }

      if (!illustrationUrl) throw new Error(ui.changeIllustrationMissing);

      if (typeof api.replaceSceneIllustration === "function") {
        await api.replaceSceneIllustration(selectedId, sceneId, {
          illustrationUrl,
          illustrationCaption: safeText(changeIllustrationModal.caption),
          illustrationCredit: safeText(changeIllustrationModal.credit),
          illustrationAltText: safeText(changeIllustrationModal.alt),
          illustrationType: "scene_illustration",
          illustrationStatus: "pending",
        });
      } else {
        await api(`/api/stories/${encodeURIComponent(selectedId)}/scenes/${encodeURIComponent(sceneId)}/illustration`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "replace",
            illustrationUrl,
            illustrationCaption: safeText(changeIllustrationModal.caption),
            illustrationCredit: safeText(changeIllustrationModal.credit),
            illustrationAltText: safeText(changeIllustrationModal.alt),
            illustrationType: "scene_illustration",
            illustrationStatus: "pending",
          }),
        });
      }

      await reloadSelectedStory();
      if (!mountedRef.current) return;
      setActionSuccess(ui.changeIllustrationSuccess);
      closeChangeIllustrationModal();
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.changeIllustrationFailed);
    } finally {
      if (mountedRef.current) {
        setWorkingAction("");
        setChangeIllustrationModal((current) => ({ ...current, uploading: false }));
      }
    }
  }

  async function rejectProposal(scene) {
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;
    if (!window.confirm(ui.confirmReject)) return;

    try {
      setWorkingAction(`reject-${sceneId}`);
      setActionError("");
      setActionSuccess("");

      const res = await api(`/api/stories/${encodeURIComponent(selectedId)}/proposals`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "rejectOne",
          proposalId: sceneId,
          sceneId,
          id: sceneId,
        }),
      });

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) {
        putStoryInCache(updated, true);
        setSelectedStory(updated);
      } else {
        await reloadSelectedStory();
      }

      if (!mountedRef.current) return;
      setActionSuccess(ui.proposalRejected);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  async function rejectAllProposals() {
    if (!selectedId || pendingProposals.length === 0) return;
    if (!window.confirm(ui.confirmRejectAll)) return;

    try {
      setWorkingAction("reject-all");
      setActionError("");
      setActionSuccess("");

      const res = await api(`/api/stories/${encodeURIComponent(selectedId)}/proposals`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "rejectAll" }),
      });

      const updated = res?.story || res;
      if (updated && (updated._id || updated.id)) {
        putStoryInCache(updated, true);
        setSelectedStory(updated);
      } else {
        await reloadSelectedStory();
      }

      if (!mountedRef.current) return;
      setActionSuccess(ui.allProposalsRejected);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  async function removeCanonScene(scene) {
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;
    if (!window.confirm(ui.confirmRemoveScene)) return;

    try {
      setWorkingAction(`remove-${sceneId}`);
      setActionError("");
      setActionSuccess("");

      await invokeTry([
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/scenes/${encodeURIComponent(sceneId)}`, {
          method: "DELETE",
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/remove-scene`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sceneId }),
        }),
      ]);

      await reloadSelectedStory();
      closeSceneModal();
      if (!mountedRef.current) return;
      setActionSuccess(ui.sceneRemoved);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  async function restoreCanonScene(scene) {
    const sceneId = safeText(scene?._id || scene?.id);
    if (!selectedId || !sceneId) return;
    if (!window.confirm(ui.confirmRestoreScene)) return;

    try {
      setWorkingAction(`restore-${sceneId}`);
      setActionError("");
      setActionSuccess("");

      await invokeTry([
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/scenes/${encodeURIComponent(sceneId)}/restore`, {
          method: "POST",
        }),
        () => api(`/api/stories/${encodeURIComponent(selectedId)}/restore-scene`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sceneId }),
        }),
      ]);

      await reloadSelectedStory();
      closeSceneModal();
      if (!mountedRef.current) return;
      setActionSuccess(ui.sceneRestored);
    } catch (e) {
      if (!mountedRef.current) return;
      setActionError(safeText(e?.message) || ui.saveErrorRoute);
    } finally {
      if (mountedRef.current) setWorkingAction("");
    }
  }

  function openWriterStudio(storyId) {
    const id = safeText(storyId || selectedId);
    if (!id) return;
    try {
      localStorage.setItem("ns.writer.focusStoryId", id);
      localStorage.setItem("ns.selectedStoryId", id);
    } catch {}
    if (typeof onNavigatePage === "function") onNavigatePage("writerStudio");
  }

  function openReaderView(storyId) {
    const id = safeText(storyId || selectedId);
    if (!id) return;
    try {
      localStorage.setItem("ns.selectedStoryId", id);
    } catch {}
    if (typeof onNavigatePage === "function") onNavigatePage("readerView");
  }

  function scrollStoryShelf(direction) {
    const el = listScrollRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.floor(el.clientWidth * 0.76));
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  }

  function scrollCanonTimeline(direction) {
    const el = canonScrollRef.current;
    if (!el) return;
    const amount = Math.max(360, Math.floor(el.clientHeight * 0.72));
    el.scrollBy({ top: direction * amount, behavior: "smooth" });
  }

  const showLeftSkeletons = loadingList && filteredStories.length === 0;
  const showRightSkeletons = (!selectedStory && (loadingList || loadingStory));

  if (!isAuthenticated) {
    return (
      <div
        className="ns-page ns-story-dashboard"
        style={{
          "--ns-dashboard-bg": `url(${process.env.PUBLIC_URL || ""}/images/storyhub/storyhub-background.png)`,
        }}
      >
        <header className="ns-page-header ns-dashboard-hero">
          <div className="ns-dashboard-hero-content">
            <p className="ns-dashboard-brand">NEXTSCENES</p>
            <p className="ns-dashboard-kicker">{isFR ? "BUREAU DE L’AUTEUR" : "AUTHOR'S LEDGER"}</p>
            <h1
              className="ns-page-title ns-dashboard-hero-title"
              style={{ margin: 0, fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              {ui.title}
            </h1>
            <p className="ns-page-subtitle ns-dashboard-hero-subtitle">{ui.loginRequired}</p>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div
      className="ns-page ns-story-dashboard ns-author-ledger"
      style={{
        "--ns-dashboard-bg": `url(${process.env.PUBLIC_URL || ""}/images/storyhub/storyhub-background.png)`,
      }}
    >
      <ProjectBreadcrumbTrail
        onNavigatePage={onNavigatePage}
        lang={lang}
        pageKey="storyDashboard"
        projectTitle={
          selectedStory?.title ||
          projectHubContext?.projectTitle
        }
      />

      <header
        className="ns-page-header ns-dashboard-hero"
        style={{
          minHeight: 0,
          height: "220px",
          maxHeight: "220px",
          padding: "18px 24px",
          marginBottom: 14,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <p
          className="ns-dashboard-kicker"
          style={{
            position: "absolute",
            top: 16,
            right: 24,
            margin: 0,
            fontSize: 11,
            letterSpacing: "0.18em",
            opacity: 0.92,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {isFR ? "BUREAU DE L’AUTEUR" : "AUTHOR'S LEDGER"}
        </p>
        <div className="ns-dashboard-hero-content" style={{ gap: 8, width: "100%" }}>
          <div className="ns-dashboard-hero-row" style={{ alignItems: "center", gap: 18 }}>
            <div style={{ minWidth: 0, flex: "1 1 560px" }}>
              <p className="ns-dashboard-brand" style={{ marginBottom: 8, fontSize: 12, letterSpacing: "0.18em" }}>NEXTSCENES</p>
              <h1
                className="ns-page-title ns-dashboard-hero-title"
                style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: "clamp(2.05rem, 3.6vw, 3.35rem)",
                  lineHeight: 1.02,
                  margin: "0 0 8px 0",
                }}
              >
                {ui.title}
              </h1>
              <p
                className="ns-page-subtitle ns-dashboard-hero-subtitle"
                style={{ maxWidth: 760, margin: 0, fontSize: "clamp(0.95rem, 1.2vw, 1.08rem)", lineHeight: 1.45 }}
              >
                {ui.subtitle}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 10,
                flexWrap: "wrap",
                flex: "0 0 auto",
              }}
            >
              <div
                style={{
                  minWidth: 142,
                  padding: "9px 11px",
                  borderRadius: 14,
                  background: "rgba(255, 255, 255, 0.12)",
                  border: "1px solid rgba(255, 250, 240, 0.22)",
                  color: "#fffaf0",
                  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.14)",
                }}
                aria-label={ui.authoredStories}
              >
                <div style={{ fontSize: 20, lineHeight: 1, fontWeight: 900 }}>
                  {filteredStories.length}
                </div>
                <div style={{ marginTop: 5, fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.88 }}>
                  {ui.authoredStories}
                </div>
                {selectedIsPublishedBook ? (
                  <div style={{ marginTop: 7, display: "grid", gap: 2, fontSize: 11, opacity: 0.9 }}>
                    <span><strong>{selectedPublicationInfo?.publicationLabel || ui.publishedBook}</strong></span>
                    <span>{selectedPublicationInfo?.developmentLabel || ui.previewOnNextScenes}</span>
                  </div>
                ) : (
                  <div style={{ marginTop: 7, display: "grid", gap: 2, fontSize: 11, opacity: 0.86 }}>
                    <span>{ui.totalCanonScenes}: <strong>{selectedStory ? canonScenes.length : "—"}</strong></span>
                    <span>{ui.activeProposals}: <strong>{selectedStory ? pendingProposals.length : "—"}</strong></span>
                  </div>
                )}
              </div>

              {typeof onNavigatePage === "function" && (
                <button
                  className="ns-button ns-button-outline ns-dashboard-hero-back"
                  type="button"
                  onClick={() => onNavigatePage("home")}
                  title={ui.back}
                  style={{
                    minHeight: 38,
                    height: 38,
                    width: "auto",
                    minWidth: 88,
                    padding: "6px 16px",
                    borderRadius: 999,
                    alignSelf: "center",
                    writingMode: "horizontal-tb",
                    transform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {ui.back}
                </button>
              )}
            </div>
          </div>

          <div className="ns-dashboard-author-note" style={{ marginTop: 8, padding: "8px 12px", maxWidth: 760, fontSize: 13 }}>
            {ui.authorAuthorityLine}
          </div>

          {(error || actionError || actionSuccess) && (
            <div className="ns-dashboard-status-stack" style={{ marginTop: 8 }}>
              {error && <p className="ns-card-meta ns-dashboard-error">{error}</p>}
              {actionError && <p className="ns-card-meta ns-dashboard-error">{actionError}</p>}
              {actionSuccess && <p className="ns-card-meta ns-dashboard-success">{actionSuccess}</p>}
            </div>
          )}
        </div>
      </header>

      <div className="ns-dashboard-ledger-layout">
        <section className="ns-card ns-dashboard-story-shelf">
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <h2 className="ns-section-title" style={{ marginBottom: 0 }}>
              {ui.authoredStories}
            </h2>

            <span className="ns-card-meta" style={{ marginLeft: "auto" }}>
              {filteredStories.length} {ui.storylistCount}
            </span>
          </div>

          <div style={{ marginTop: 6, marginBottom: 6, display: "grid", gap: 6 }}>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={ui.searchPlaceholder}
              className="ns-input"
              style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)" }}
            />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="ns-button ns-button-outline" type="button" onClick={() => { setSearchInput(""); setSearchQuery(""); }} style={{ minHeight: 36 }}>
                {ui.clear}
              </button>

              <button className="ns-button ns-button-outline" type="button" onClick={() => { setActionError(""); setActionSuccess(""); reloadSelectedStory(); }} disabled={!selectedId || loadingStory} style={{ minHeight: 36 }}>
                {ui.refresh}
              </button>

              <div className="ns-card-meta" style={{ marginLeft: "auto" }}>
                {ui.authoredByYou}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0, 1fr) 42px", gap: 10, alignItems: "center" }}>
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() => scrollStoryShelf(-1)}
              aria-label={isFR ? "Faire défiler à gauche" : "Scroll left"}
              style={{ minHeight: 40, width: 40, padding: 0, borderRadius: 999, fontSize: 18 }}
            >
              ‹
            </button>

            <div
              ref={listScrollRef}
              className="ns-dashboard-story-strip"
              style={{ overflowX: "auto", scrollBehavior: "smooth", paddingBottom: 8 }}
            >
            {showLeftSkeletons ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => <SkeletonListItem key={`sd-left-${i}`} />)}
              </>
            ) : displayedStories.length === 0 ? (
              <div className="ns-card ns-card-soft">
                <p className="ns-card-title" style={{ marginBottom: 8 }}>{ui.noStories}</p>
                <p className="ns-card-meta" style={{ marginBottom: 10 }}>
                  {authoredStories.length === 0 ? ui.noAuthorMatches : ui.noStoriesHint}
                </p>
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("storyHub") : null)}
                  style={{ minHeight: 40 }}
                >
                  {ui.storyHubStart}
                </button>
              </div>
            ) : (
              displayedStories.map((story) => {
                const id = getId(story);
                const active = Boolean(id && selectedId && String(id) === String(selectedId));
                const publicationInfo = getPublicationInfo(story, lang);
                const isPublishedBook = Boolean(publicationInfo?.isPublished);
                const author = storyAuthorInfo(story);
                const displayAuthorName = isPublishedBook
                  ? (publicationInfo.publishedAuthorName || author.name || author.username || ui.unknown)
                  : (author.name || author.username || ui.unknown);
                const stateLabel =
                  normalizeProposalState(story) === "proposal" ? ui.proposalState : ui.canonState;

                return (
                  <div
                    key={String(id || story?.title || Math.random())}
                    ref={active ? selectedItemRef : null}
                    className={"ns-card ns-clickable ns-dashboard-authored-card" + (active ? " ns-card-soft is-active" : "")}
                    style={{
                      flex: "0 0 292px",
                      minHeight: 176,
                      textAlign: "left",
                      padding: 12,
                      border: active ? "2px solid rgba(16,185,129,0.55)" : "1px solid rgba(0,0,0,0.08)",
                      boxShadow: active ? "0 12px 28px rgba(16,185,129,0.14)" : "0 8px 22px rgba(15,23,42,0.07)",
                      background: active ? "rgba(240, 253, 250, 0.72)" : "rgba(255,255,255,0.94)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => openStory(id)}
                      disabled={!id || loadingStory}
                      aria-disabled={!id || loadingStory ? "true" : "false"}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: !id || loadingStory ? "not-allowed" : "pointer",
                      }}
                      title={ui.openStorylineHint}
                    >
                      <p className="ns-card-title" style={{ marginBottom: 6, fontSize: 16, lineHeight: 1.15 }}>
                        {safeText(story?.title) || "Untitled Storyline"}
                      </p>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                        {isPublishedBook ? (
                          <>
                            <Pill
                              label={publicationInfo.publicationLabel || ui.publishedBook}
                              bg="rgba(255,247,237,0.96)"
                              border="rgba(194,65,12,0.28)"
                              text="rgba(154,52,18,0.98)"
                              style={{ fontSize: 12, padding: "4px 8px" }}
                            />
                            <Pill
                              label={publicationInfo.developmentLabel || ui.previewOnNextScenes}
                              bg="rgba(236,253,245,0.96)"
                              border="rgba(16,185,129,0.28)"
                              text="rgba(4,120,87,0.98)"
                              style={{ fontSize: 12, padding: "4px 8px" }}
                            />
                          </>
                        ) : (
                          <>
                            <ModePill mode={story?.writingMode} ui={ui} style={{ fontSize: 12, padding: "4px 8px" }} />
                            <Pill
                              label={stateLabel}
                              bg="rgba(241,245,249,0.95)"
                              border="rgba(100,116,139,0.20)"
                              text="rgba(51,65,85,0.92)"
                              style={{ fontSize: 12, padding: "4px 8px" }}
                            />
                          </>
                        )}
                      </div>

                      <p className="ns-card-meta" style={{ marginBottom: 3 }}>
                        {ui.by} {displayAuthorName}
                      </p>

                      <p className="ns-card-meta" style={{ marginBottom: 4, opacity: 0.8 }}>
                        {story?.forum || ui.unknown} · {formatRelativeTime(story?.updatedAt || story?.createdAt, lang)}
                      </p>

                      <p
                        className="ns-card-meta"
                        style={{
                          marginBottom: 0,
                          lineHeight: "1.35",
                          opacity: 0.9,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {clampWords(guessDescription(story), 18) || ui.openStorylineHint}
                      </p>
                    </button>

                    {active && (
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span className="ns-card-meta">{ui.selected}</span>
                        {isPublishedBook ? (
                          <>
                            <button className="ns-button ns-button-outline" type="button" onClick={() => openReaderView(id)} style={{ minHeight: 32, padding: "4px 8px", fontSize: 13 }}>
                              {ui.viewPublishedBook}
                            </button>
                            {publicationInfo.amazonHref ? (
                              <button className="ns-button ns-button-outline ns-amazon-action" type="button" onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")} style={{ minHeight: 32, padding: "4px 8px", fontSize: 13 }}>
                                {ui.viewOnAmazon}
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <button className="ns-button ns-button-outline" type="button" onClick={() => openWriterStudio(id)} style={{ minHeight: 32, padding: "4px 8px", fontSize: 13 }}>
                            {ui.openInWriterStudio}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {!showLeftSkeletons && hasMoreStories && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 4, marginBottom: 10 }}>
                <button className="ns-button ns-button-outline" type="button" onClick={() => setVisibleCount((prev) => prev + STORY_LIST_PAGE_SIZE)} style={{ minHeight: 40 }}>
                  {ui.loadMore}
                </button>
              </div>
            )}
            </div>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() => scrollStoryShelf(1)}
              aria-label={isFR ? "Faire défiler à droite" : "Scroll right"}
              style={{ minHeight: 40, width: 40, padding: 0, borderRadius: 999, fontSize: 18 }}
            >
              ›
            </button>
          </div>
        </section>

        <section className="ns-card ns-dashboard-detail-panel" key={String(selectedId || "empty")} style={{ minHeight: 240 }}>
          {showRightSkeletons ? (
            <SkeletonRightPanel />
          ) : !selectedStory ? (
            <div className="ns-reader-orientationCard">
              <div className="ns-reader-orientationTitle">{ui.dashboardWelcome}</div>
              <p className="ns-card-meta" style={{ marginBottom: 10 }}>
                {ui.dashboardIntro}
              </p>
              <div className="ns-reader-orientationPaths">
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("storyHub") : null)}
                  style={{ minHeight: 40 }}
                >
                  {ui.storyHubStart}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div
                className="ns-card ns-card-soft"
                style={{
                  marginBottom: 12,
                  background: "rgba(16,185,129,0.10)",
                  border: "1px solid rgba(16,185,129,0.18)",
                }}
              >
                <div className="ns-card-title" style={{ marginBottom: 8 }}>
                  {selectedStructured?.title || "Storyline"}
                  {loadingStory ? ` · ${ui.loading}` : ""}
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                  {selectedIsPublishedBook ? (
                    <>
                      <Pill
                        label={selectedPublicationInfo?.publicationLabel || ui.publishedBook}
                        bg="rgba(255,247,237,0.96)"
                        border="rgba(194,65,12,0.28)"
                        text="rgba(154,52,18,0.98)"
                      />
                      <Pill
                        label={selectedPublicationInfo?.developmentLabel || ui.previewOnNextScenes}
                        bg="rgba(236,253,245,0.96)"
                        border="rgba(16,185,129,0.28)"
                        text="rgba(4,120,87,0.98)"
                      />
                    </>
                  ) : (
                    <>
                      <ModePill mode={selectedStructured?.writingMode} ui={ui} />
                      <Pill
                        label={selectedStructured?.proposalState === "proposal" ? ui.proposalState : ui.canonState}
                        bg="rgba(241,245,249,0.95)"
                        border="rgba(100,116,139,0.20)"
                        text="rgba(51,65,85,0.92)"
                      />
                      <Pill
                        label={ui.statusReady}
                        bg="rgba(239,246,255,0.95)"
                        border="rgba(59,130,246,0.28)"
                        text="rgba(30,64,175,0.95)"
                      />
                    </>
                  )}
                </div>

                {selectedIsPublishedBook ? (
                  <div className="ns-card ns-card-soft" style={{ marginBottom: 12, padding: 12, background: "rgba(255,251,235,0.72)", border: "1px solid rgba(194,65,12,0.18)", color: "#78350f", fontWeight: 750 }}>
                    {ui.publishedDashboardNote}
                  </div>
                ) : null}

                {sharedProjectContextPilot ? (
                  <div
                    className="ns-card ns-card-soft ns-shared-project-context-pilot"
                    style={{
                      marginBottom: 12,
                      padding: 12,
                      background: "rgba(239,246,255,0.72)",
                      border: "1px solid rgba(59,130,246,0.20)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div className="ns-card-title" style={{ marginBottom: 3 }}>
                          {isFR
                            ? "Contexte partagé du projet"
                            : "Shared project context"}
                        </div>

                        <div className="ns-card-meta">
                          {isFR
                            ? "Pilote en lecture seule. Aucune donnée du projet n’est modifiée."
                            : "Read-only pilot. No project data is changed."}
                        </div>
                      </div>

                      <Pill
                        label={isFR ? "Lecture seule" : "Read only"}
                        bg="rgba(219,234,254,0.96)"
                        border="rgba(37,99,235,0.24)"
                        text="rgba(30,64,175,0.96)"
                      />
                    </div>

                    {sharedProjectContextLoading ? (
                      <p className="ns-card-meta">
                        {isFR
                          ? "Chargement du contexte du projet…"
                          : "Loading project context…"}
                      </p>
                    ) : sharedProjectContextError ? (
                      <p className="ns-card-meta" style={{ color: "#991b1b" }}>
                        {sharedProjectContextError}
                      </p>
                    ) : sharedProjectContext ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div className="ns-card-meta">
                            <strong>{isFR ? "Type de projet" : "Project type"}</strong>
                          </div>
                          <div>
                            {sharedContextLabel(
                              sharedProjectContext?.project?.projectType,
                              "projectType",
                              isFR
                            ) || ui.unknown}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>{isFR ? "Langue" : "Language"}</strong>
                          </div>
                          <div>
                            {safeText(
                              sharedProjectContext?.project?.language
                            ) || ui.unknown}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>{isFR ? "Visibilité" : "Visibility"}</strong>
                          </div>
                          <div>
                            {sharedContextLabel(
                              sharedProjectContext?.project?.visibilityStatus,
                              "visibility",
                              isFR
                            ) || ui.unknown}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>
                              {isFR ? "Phase du workflow" : "Workflow phase"}
                            </strong>
                          </div>
                          <div>
                            {sharedContextLabel(
                              sharedProjectContext?.workflow?.state?.phase,
                              "workflow",
                              isFR
                            ) || ui.unknown}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>{isFR ? "Membres" : "Members"}</strong>
                          </div>
                          <div>
                            {safeArray(
                              sharedProjectContext?.memberships
                            ).length}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>{isFR ? "Versions" : "Versions"}</strong>
                          </div>
                          <div>
                            {Number(
                              sharedProjectContext?.contentSummary?.total
                            ) || 0}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>
                              {isFR ? "Récupérables" : "Recoverable"}
                            </strong>
                          </div>
                          <div>
                            {Number(
                              sharedProjectContext?.contentSummary?.recoverable
                            ) || 0}
                          </div>
                        </div>

                        <div>
                          <div className="ns-card-meta">
                            <strong>
                              {isFR
                                ? "Transitions permises"
                                : "Permitted transitions"}
                            </strong>
                          </div>
                          <div>
                            {safeArray(
                              sharedProjectContext?.workflow
                                ?.permittedTransitions
                            ).length > 0
                              ? safeArray(
                                  sharedProjectContext?.workflow
                                    ?.permittedTransitions
                                )
                                  .map((transition) =>
                                    sharedContextLabel(
                                      transition,
                                      "transition",
                                      isFR
                                    )
                                  )
                                  .join(", ")
                              : isFR
                                ? "Aucune"
                                : "None"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="ns-card-meta">
                        {isFR
                          ? "Aucun contexte de projet disponible."
                          : "No project context is available."}
                      </p>
                    )}
                  </div>
                ) : null}
                {!selectedIsPublishedBook ? (


                <div
                  className="ns-card ns-card-soft"
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    background: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(15,23,42,0.08)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 6 }}><strong>{ui.totalCanonScenes}</strong></div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{canonScenes.length}</div>
                    </div>
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 6 }}><strong>{ui.activeProposals}</strong></div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{pendingProposals.length}</div>
                    </div>
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 6 }}><strong>{ui.contributors}</strong></div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{selectedStructured?.contributors ?? 0}</div>
                    </div>
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 6 }}><strong>{ui.lastActivity}</strong></div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                        {formatRelativeTime(selectedStructured?.lastActivityAt || selectedStructured?.updatedAt || selectedStructured?.createdAt, lang) || ui.unknown}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12 }}>
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 4 }}>{ui.storyPulse}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{selectedStructured?.pulseLabel || ui.unknown}</div>
                    </div>
                    <div>
                      <div className="ns-card-meta" style={{ marginBottom: 4 }}>{ui.currentDirection}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{selectedStructured?.directionLabel || ui.unknown}</div>
                    </div>
                  </div>
                </div>
                ) : null}

                <div
                  className="ns-card ns-card-soft ns-story-dashboard-detail-collapser"
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    background: "rgba(255,255,255,0.68)",
                    border: "1px solid rgba(15,23,42,0.08)",
                  }}
                >
                  <div className="ns-story-dashboard-detail-grid">
                    <details className="ns-dashboard-mini-details">
                      <summary>
                        <strong>{ui.description}</strong>
                        <span>{clampWords(selectedStructured?.description, 12) || ui.noDescriptionYet}</span>
                      </summary>
                      <p>{safeText(selectedStructured?.description) || ui.noDescriptionYet}</p>
                    </details>
                    <details className="ns-dashboard-mini-details">
                      <summary>
                        <strong>{ui.synopsis}</strong>
                        <span>{clampWords(selectedStructured?.synopsis, 12) || ui.noSynopsisYet}</span>
                      </summary>
                      <p>{safeText(selectedStructured?.synopsis) || ui.noSynopsisYet}</p>
                    </details>
                    <details className="ns-dashboard-mini-details">
                      <summary>
                        <strong>{ui.aboutAuthor}</strong>
                        <span>{clampWords(selectedStructured?.aboutAuthor, 12) || ui.noAuthorBioYet}</span>
                      </summary>
                      <p>{safeText(selectedStructured?.aboutAuthor) || ui.noAuthorBioYet}</p>
                    </details>
                  </div>
                </div>

                <div className="ns-dashboard-actions">
                  {!selectedIsPublishedBook ? (
                    <button className="ns-button ns-button-outline" type="button" onClick={() => openWriterStudio()} style={{ minHeight: 40 }}>
                      {ui.openInWriterStudio}
                    </button>
                  ) : null}
                  <button className="ns-button ns-button-outline" type="button" onClick={() => openReaderView()} style={{ minHeight: 40 }}>
                    {selectedIsPublishedBook ? ui.viewPublishedBook : ui.openInReaderView}
                  </button>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => setOwnerSettingsOpen(true)}
                    disabled={!selectedId}
                    style={{ minHeight: 40 }}
                  >
                    {isFR ? "Paramètres de la storyline" : "Story Settings"}
                  </button>
                  {!selectedIsPublishedBook ? (
                    <button
                      className="ns-button ns-button-outline ns-editorial-board-open-button"
                      type="button"
                      onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("editorialBoard", { storyId: selectedId }) : null)}
                      disabled={!selectedId || canonScenes.length === 0}
                      style={{ minHeight: 40 }}
                    >
                      {isFR ? "Ouvrir le comité éditorial" : "Open Editorial Board"}
                    </button>
                  ) : null}
                  {selectedIsPublishedBook && selectedPublicationInfo?.amazonHref ? (
                    <button className="ns-button ns-button-outline ns-amazon-action" type="button" onClick={() => window.open(selectedPublicationInfo.amazonHref, "_blank", "noopener,noreferrer")} style={{ minHeight: 40 }}>
                      {ui.viewOnAmazon}
                    </button>
                  ) : null}
                  {!selectedIsPublishedBook ? (
                    <>
                      <button className="ns-button ns-button-outline" type="button" onClick={openEditModal} style={{ minHeight: 40 }}>
                        {ui.editDetails}
                      </button>
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() => switchProposalMode(selectedStructured?.proposalState === "proposal" ? "canon" : "proposal")}
                        disabled={Boolean(workingAction)}
                        style={{ minHeight: 40 }}
                      >
                        {selectedStructured?.proposalState === "proposal" ? ui.modeSwitchCanon : ui.modeSwitchProposal}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <details hidden={selectedIsPublishedBook} className="ns-card ns-card-soft" style={{ marginBottom: 12, padding: 12 }}>
                <summary style={{ cursor: "pointer", fontWeight: 900, color: "#064e3b" }}>
                  {ui.generateManuscriptTitle} / {ui.contributionRecordsTitle}
                </summary>
                <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>{ui.generateManuscriptBody}</p>
                  <PolicyLinksCard
                    lang={lang}
                    title={ui.policyLinksTitle}
                    body={ui.policyLinksBody}
                    compact
                  />
                  <label className="ns-manuscript-checkbox">
                    <input
                      type="checkbox"
                      checked={includeContributorAppendix}
                      onChange={(e) => setIncludeContributorAppendix(e.target.checked)}
                    />
                    <span>
                      <strong>{ui.includeContributorAppendix}</strong>
                      <em>{ui.includeContributorAppendixHelp}</em>
                    </span>
                  </label>
                  <div className="ns-manuscript-actions">
                    <button
                      className="ns-button ns-button-primary"
                      type="button"
                      onClick={handleGenerateManuscriptDocx}
                      disabled={canonScenes.length === 0}
                    >
                      {ui.generateDocx}
                    </button>
                    <span className="ns-manuscript-help">{ui.manuscriptReadyHelp}</span>
                  </div>

                  <div className="ns-manuscript-editor-card ns-manuscript-editor-bridge" role="region" aria-label={isFR ? "Comité éditorial" : "Editorial Board"}>
                    <div className="ns-manuscript-editor-copy">
                      <strong>{isFR ? "Comité éditorial" : "Editorial Board"}</strong>
                      <span>
                        {isFR
                          ? "La carte de l’âme, le registre de continuité, les passes éditoriales et les brouillons séparés vivent maintenant sur une page dédiée. Le tableau de bord reste léger."
                          : "The Book Soul Map, Continuity Ledger, chapter passes, and separate editorial drafts now live on a focused page. The Story Dashboard stays light."}
                      </span>
                    </div>
                    <button
                      className="ns-button ns-button-outline ns-manuscript-editor-main-button"
                      type="button"
                      onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("editorialBoard", { storyId: selectedId }) : null)}
                      disabled={!selectedId || canonScenes.length === 0}
                    >
                      {isFR ? "Ouvrir le comité éditorial" : "Open Editorial Board"}
                    </button>
                  </div>
                </div>
              </details>

              <details hidden={selectedIsPublishedBook} className="ns-card ns-card-soft" style={{ marginBottom: 12, padding: 12 }}>
                <summary style={{ cursor: "pointer", fontWeight: 900, color: "#0f172a" }}>
                  {ui.storyIdentity} / {ui.storyIntelligence}
                </summary>
                <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                  <div className="ns-card-meta"><strong>{ui.titleLabel}</strong> = {selectedStructured?.title || ui.unknown}</div>
                  <div className="ns-card-meta"><strong>{ui.forum}</strong> = {selectedStructured?.forum || ui.unknown}</div>
                  <div className="ns-card-meta"><strong>{ui.writingMode}</strong> = {modeMeta(selectedStructured?.writingMode, ui).label}</div>
                  <div className="ns-card-meta"><strong>{ui.currentState}</strong> = {selectedStructured?.proposalState === "proposal" ? ui.proposalState : ui.canonState}</div>
                  <div className="ns-card-meta"><strong>{ui.createdAt}</strong> = {formatDateTime(selectedStructured?.createdAt, lang) || ui.unknown}</div>
                  <div className="ns-card-meta"><strong>{ui.updatedAt}</strong> = {formatDateTime(selectedStructured?.updatedAt, lang) || ui.unknown}</div>
                  <div className="ns-card-meta"><strong>{ui.rejectedProposals}</strong> = {rejectedProposals.length}</div>
                  <div className="ns-card-meta"><strong>{ui.readers}</strong> = {countReadersFromStory(selectedStory)}</div>
                  <div className="ns-card-meta"><strong>{ui.opens}</strong> = {countViewsFromStory(selectedStory)}</div>
                </div>
              </details>

              <div
                style={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "12px 14px",
                  margin: "14px 0 10px",
                  borderRadius: 16,
                  background: "linear-gradient(135deg, rgba(6,95,70,0.10), rgba(255,255,255,0.96))",
                  border: "1px solid rgba(16,185,129,0.26)",
                  display: selectedIsPublishedBook ? "none" : "flex",
                }}
              >
                <div>
                  <h2 className="ns-section-title" style={{ margin: 0, letterSpacing: "0.04em" }}>
                    {ui.canonTimeline}
                  </h2>
                  <p className="ns-card-meta" style={{ margin: "4px 0 0" }}>
                    {ui.totalCanonScenes}: <strong>{canonScenes.length}</strong> · {ui.contributors}: <strong>{countStoryContributors(selectedStory, ui)}</strong>
                  </p>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button className="ns-button ns-button-outline" type="button" onClick={() => scrollCanonTimeline(-1)} style={{ minHeight: 34, width: 38, padding: 0, borderRadius: 999 }} aria-label={isFR ? "Remonter le canon" : "Scroll canon up"}>
                    ↑
                  </button>
                  <button className="ns-button ns-button-outline" type="button" onClick={() => scrollCanonTimeline(1)} style={{ minHeight: 34, width: 38, padding: 0, borderRadius: 999 }} aria-label={isFR ? "Descendre le canon" : "Scroll canon down"}>
                    ↓
                  </button>
                </div>
              </div>
              <div
                ref={canonScrollRef}
                className="ns-card ns-card-soft"
                style={{
                  marginBottom: 12,
                  maxHeight: "760px",
                  overflowY: "auto",
                  paddingRight: 10,
                  border: "2px solid rgba(16,185,129,0.22)",
                  boxShadow: "0 18px 42px rgba(15,23,42,0.10)",
                  background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(240,253,250,0.42))",
                  display: selectedIsPublishedBook ? "none" : undefined,
                }}
              >
                <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      value={canonSearchInput}
                      onChange={(e) => setCanonSearchInput(e.target.value)}
                      placeholder={`${ui.canonSearchPlaceholder} (${ui.sceneSearchHint})`}
                      className="ns-input"
                      style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", minWidth: 240, flex: "1 1 260px" }}
                    />

                    <select
                      value={canonContributorFilter}
                      onChange={(e) => setCanonContributorFilter(e.target.value)}
                      className="ns-input"
                      style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", minWidth: 220 }}
                      title={ui.canonContributorFilter}
                    >
                      <option value="all">{ui.allContributors}</option>
                      {canonContributors.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="ns-card-meta"><strong>{ui.jumpToScene}</strong></span>
                      <input
                        value={canonJumpInput}
                        onChange={(e) => { setCanonJumpInput(e.target.value); setCanonJumpError(""); }}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="347"
                        className="ns-input"
                        style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", width: 110 }}
                      />
                      <button className="ns-button ns-button-outline" type="button" onClick={jumpToCanonScene} style={{ minHeight: 36 }}>
                        {ui.jump}
                      </button>
                    </div>

                    <button className="ns-button ns-button-outline" type="button" onClick={clearCanonControls} style={{ minHeight: 36 }}>
                      {ui.clearCanonFilters}
                    </button>

                    <div className="ns-card-meta" style={{ marginLeft: "auto" }}>
                      {ui.showingScenesRange} <strong>{canonRangeStart}</strong>–<strong>{canonRangeEnd}</strong> {ui.ofCanonFiltered} <strong>{filteredCanonScenes.length}</strong> · {ui.ofCanonTotal} <strong>{canonScenes.length}</strong>
                    </div>
                  </div>

                  {canonJumpError ? (
                    <p className="ns-card-meta" style={{ color: "#b91c1c", marginBottom: 0 }}>{canonJumpError}</p>
                  ) : null}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage(1)} disabled={canonPage <= 1} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                      {ui.first}
                    </button>
                    <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage((p) => Math.max(1, p - 1))} disabled={canonPage <= 1} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                      {ui.previous}
                    </button>
                    <span className="ns-card-meta">
                      {ui.pageLabel} <strong>{canonPage}</strong> / <strong>{canonPageCount}</strong>
                    </span>
                    <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage((p) => Math.min(canonPageCount, p + 1))} disabled={canonPage >= canonPageCount} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                      {ui.next}
                    </button>
                    <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage(canonPageCount)} disabled={canonPage >= canonPageCount} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                      {ui.last}
                    </button>
                  </div>
                </div>

                {canonScenes.length === 0 ? (
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>{ui.noCanonScenes}</p>
                ) : filteredCanonScenes.length === 0 ? (
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>{ui.noCanonMatch}</p>
                ) : (
                  <>
                    {pagedCanonScenes.map((scene, index) => {
                      const sceneId = sceneStableId(scene);
                      const ratingSummary = sceneId ? sceneRatingSummaries[sceneId] : null;
                      const completionSummary = sceneId ? sceneCompletionSummaries[sceneId] : null;
                      const reactionSummary = sceneId ? sceneReactionSummaries[sceneId] : null;
                      const statusMeta = sceneStatusPillMeta("canon", ui);
                      const absoluteNumber = canonSceneNumberMap.get(sceneId) || ((canonPage - 1) * CANON_PAGE_SIZE + index + 1);
                      const contributor = safeSceneContributor(scene, ui);
                      return (
                        <div
                          key={sceneId || `canon-${absoluteNumber}`}
                          style={{
                            padding: "12px 12px",
                            marginBottom: 8,
                            borderRadius: 14,
                            background: index % 2 === 0 ? "rgba(248,250,252,0.92)" : "rgba(240,253,250,0.72)",
                            border: "1px solid rgba(15,23,42,0.08)",
                            borderLeft: "4px solid rgba(16,185,129,0.45)",
                          }}
                        >
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                              {ui.scene} {absoluteNumber}
                            </div>
                            <Pill {...statusMeta} style={{ fontSize: 12, padding: "4px 8px" }} />
                            <span className="ns-card-meta">{ui.contributor}: {contributor}</span>
                            <span className="ns-card-meta">{formatDateTime(scene?.createdAt || scene?.updatedAt, lang)}</span>
                          </div>

                          <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                            <strong>{safeSceneTitle(scene, ui)}</strong>
                          </p>

                          <div style={{ lineHeight: "1.55", color: "#334155", marginBottom: 8 }}>
                            {clampWords(safeSceneText(scene), 24) || ui.unknown}
                          </div>

                          <div className="ns-dashboard-scene-signal-row">
                            <SceneRatingSummaryPill summary={ratingSummary} loading={sceneRatingsLoading} ui={ui} />
                            <SceneCompletionSummaryPill summary={completionSummary} loading={sceneCompletionLoading} ui={ui} />
                            <SceneReactionSummaryPill summary={reactionSummary} loading={sceneReactionsLoading} ui={ui} />
                          </div>

                          <SceneIllustrationDashboard
                            scene={scene}
                            ui={ui}
                            onApprove={approveSceneIllustration}
                            onReject={rejectSceneIllustration}
                            onChangeImage={openChangeIllustrationModal}
                            workingAction={workingAction}
                          />

                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                            <button className="ns-button ns-button-outline" type="button" onClick={() => openSceneModal(scene, "canon", absoluteNumber)} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                              {ui.viewScene}
                            </button>
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              onClick={() => removeCanonScene(scene)}
                              disabled={workingAction === `remove-${sceneId}`}
                              style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}
                            >
                              {workingAction === `remove-${sceneId}` ? ui.working : ui.removeScene}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
                      <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage(1)} disabled={canonPage <= 1} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                        {ui.first}
                      </button>
                      <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage((p) => Math.max(1, p - 1))} disabled={canonPage <= 1} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                        {ui.previous}
                      </button>
                      <span className="ns-card-meta">
                        {ui.pageLabel} <strong>{canonPage}</strong> / <strong>{canonPageCount}</strong>
                      </span>
                      <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage((p) => Math.min(canonPageCount, p + 1))} disabled={canonPage >= canonPageCount} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                        {ui.next}
                      </button>
                      <button className="ns-button ns-button-outline" type="button" onClick={() => setCanonPage(canonPageCount)} disabled={canonPage >= canonPageCount} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                        {ui.last}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {sectionTitle(ui.removedCanonScenesTitle)}
              <div className="ns-card ns-card-soft" style={{ marginBottom: 12 }}>
                <p className="ns-card-meta" style={{ marginTop: 0 }}>
                  {ui.removedCanonScenesHelp}
                </p>

                {removedCanon.length === 0 ? (
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>
                    {ui.noRemovedCanonScenes}
                  </p>
                ) : (
                  removedCanon.map((scene, index) => {
                    const sceneId = sceneStableId(scene);
                    return (
                      <div
                        key={sceneId || `removed-canon-${index}`}
                        style={{
                          padding: "12px",
                          marginBottom: 8,
                          borderRadius: 14,
                          background: "rgba(254,242,242,0.72)",
                          border: "1px solid rgba(185,28,28,0.14)",
                          borderLeft: "4px solid rgba(185,28,28,0.42)",
                        }}
                      >
                        <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                          <strong>{safeSceneTitle(scene, ui)}</strong>
                        </p>

                        <div style={{ lineHeight: "1.55", color: "#334155", marginBottom: 8 }}>
                          {clampWords(safeSceneText(scene), 24) || ui.unknown}
                        </div>

                        <div className="ns-card-meta" style={{ marginBottom: 8 }}>
                          {formatDateTime(scene?.removedAt || scene?.updatedAt, lang)}
                        </div>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          onClick={() => restoreCanonScene(scene)}
                          disabled={workingAction === `restore-${sceneId}`}
                          style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}
                        >
                          {workingAction === `restore-${sceneId}` ? ui.working : ui.restoreScene}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {!selectedIsPublishedBook ? sectionTitle(ui.proposalChamber) : null}
              <div className="ns-card ns-card-soft" style={{ marginBottom: 12, display: selectedIsPublishedBook ? "none" : undefined }}>
                {pendingProposals.length === 0 ? (
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>{ui.proposalQueueEmpty}</p>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <button className="ns-button ns-button-outline" type="button" onClick={rejectAllProposals} disabled={workingAction === "reject-all"} style={{ minHeight: 36 }}>
                        {workingAction === "reject-all" ? ui.working : ui.rejectAll}
                      </button>
                    </div>

                    {pendingProposals.map((scene, index) => {
                      const sceneId = safeText(scene?._id || scene?.id);
                      const statusMeta = sceneStatusPillMeta("pending", ui);
                      const proposer =
                        safeText(scene?.authorName || scene?.createdByName || scene?.writerName || scene?.username || scene?.authorUsername) ||
                        ui.unknown;

                      return (
                        <div
                          key={sceneId || `proposal-${index}`}
                          style={{
                            padding: "10px 0",
                            borderBottom: index === pendingProposals.length - 1 ? "none" : "1px solid rgba(15,23,42,0.08)",
                          }}
                        >
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
                            <div style={{ fontWeight: 800, color: "#0f172a" }}>
                              {ui.proposal} {index + 1}
                            </div>
                            <Pill {...statusMeta} style={{ fontSize: 12, padding: "4px 8px" }} />
                            <span className="ns-card-meta">{ui.by} {proposer}</span>
                            <span className="ns-card-meta">{ui.submitted} {formatDateTime(scene?.createdAt || scene?.updatedAt, lang)}</span>
                          </div>

                          <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                            <strong>{safeSceneTitle(scene, ui)}</strong>
                          </p>

                          <div style={{ lineHeight: "1.55", color: "#334155", marginBottom: 8 }}>
                            {clampWords(safeSceneText(scene), 36) || ui.unknown}
                          </div>

                          <SceneIllustrationDashboard
                            scene={scene}
                            ui={ui}
                            onApprove={approveSceneIllustration}
                            onReject={rejectSceneIllustration}
                            onChangeImage={openChangeIllustrationModal}
                            workingAction={workingAction}
                          />

                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                            <button className="ns-button ns-button-outline" type="button" onClick={() => openSceneModal(scene, "proposal", null)} style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}>
                              {ui.inspectScene}
                            </button>
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              onClick={() => approveProposal(scene)}
                              disabled={workingAction === `approve-${sceneId}`}
                              style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}
                            >
                              {workingAction === `approve-${sceneId}` ? ui.working : ui.approve}
                            </button>
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              onClick={() => rejectProposal(scene)}
                              disabled={workingAction === `reject-${sceneId}`}
                              style={{ minHeight: 34, padding: "5px 10px", fontSize: 13 }}
                            >
                              {workingAction === `reject-${sceneId}` ? ui.working : ui.reject}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {!selectedIsPublishedBook ? sectionTitle(ui.rejectedHistory) : null}
              <div className="ns-card ns-card-soft" style={{ marginBottom: 12 }}>
                {rejectedProposals.length === 0 ? (
                  <p className="ns-card-meta" style={{ marginBottom: 0 }}>{ui.noRejected}</p>
                ) : (
                  rejectedProposals.slice(0, 10).map((scene, index) => (
                    <div
                      key={safeText(scene?._id || scene?.id || `rejected-${index}`)}
                      style={{
                        padding: "10px 0",
                        borderBottom: index === Math.min(rejectedProposals.length, 10) - 1 ? "none" : "1px solid rgba(15,23,42,0.08)",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ fontWeight: 800, color: "#0f172a" }}>{ui.proposal} {index + 1}</div>
                        <Pill {...sceneStatusPillMeta("rejected", ui)} style={{ fontSize: 12, padding: "4px 8px" }} />
                        <span className="ns-card-meta">{formatDateTime(scene?.updatedAt || scene?.createdAt, lang)}</span>
                      </div>
                      <div style={{ lineHeight: "1.55", color: "#334155" }}>
                        {clampWords(safeSceneText(scene), 28) || ui.unknown}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {approveNoteModal.open && approveNoteModal.scene && (
        <div
          role="dialog"
          aria-modal="true"
          className="ns-modal-backdrop"
          onClick={closeApproveNoteModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1250,
          }}
        >
          <div
            className="ns-card ns-card-soft"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(640px, 100%)", padding: 18, borderRadius: 18, background: "#ffffff" }}
          >
            <h2 className="ns-section-title" style={{ marginTop: 0, marginBottom: 8 }}>
              {ui.approveDecisionNoteTitle}
            </h2>
            <p className="ns-card-meta" style={{ marginBottom: 12 }}>{ui.approveDecisionNoteHelp}</p>
            <div style={{ marginBottom: 10 }}>
              <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                <strong>{safeSceneTitle(approveNoteModal.scene, ui)}</strong>
              </p>
              <div style={{ lineHeight: "1.55", color: "#334155" }}>
                {clampWords(safeSceneText(approveNoteModal.scene), 34) || ui.unknown}
              </div>
            </div>
            <textarea
              value={approveNoteModal.note}
              onChange={(e) => setApproveNoteModal((current) => ({ ...current, note: e.target.value }))}
              placeholder={ui.approveDecisionNotePlaceholder}
              style={{
                width: "100%",
                minHeight: 120,
                resize: "vertical",
                borderRadius: 14,
                border: "1px solid rgba(15,23,42,0.16)",
                padding: 12,
                fontSize: 15,
                lineHeight: 1.55,
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button className="ns-button ns-button-outline" type="button" onClick={closeApproveNoteModal} disabled={Boolean(workingAction)}>
                {ui.cancel}
              </button>
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={confirmApproveProposal}
                disabled={Boolean(workingAction) || !safeText(approveNoteModal.note)}
              >
                {workingAction ? ui.working : ui.approveNow}
              </button>
            </div>
          </div>
        </div>
      )}

      {approveIllustrationModal.open && approveIllustrationModal.scene && (() => {
        const art = safeSceneIllustration(approveIllustrationModal.scene);
        const sceneId = safeText(approveIllustrationModal.scene?._id || approveIllustrationModal.scene?.id);
        return (
          <div
            role="dialog"
            aria-modal="true"
            className="ns-modal-backdrop ns-illustration-modal-backdrop"
            onClick={closeApproveIllustrationModal}
          >
            <div className="ns-card ns-illustration-approval-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ns-illustration-modal-head">
                <div>
                  <h2 className="ns-section-title">{ui.illustrationApproveTitle}</h2>
                  <p className="ns-card-meta">{ui.illustrationApproveHelp}</p>
                </div>
                <Pill
                  label={ui.illustrationPublicDisplay}
                  bg="rgba(226,248,242,0.92)"
                  border="rgba(16,185,129,0.42)"
                  text="rgba(6,95,70,0.95)"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                />
              </div>

              {art ? (
                <figure className="ns-scene-illustration-public ns-illustration-approval-figure">
                  <div className="ns-scene-illustration-public-frame">
                    <img src={art.url} alt={art.alt || ui.sceneIllustration} loading="lazy" />
                  </div>
                  {(art.caption || art.credit) ? (
                    <figcaption className="ns-scene-illustration-public-caption">
                      {art.caption ? <span className="ns-scene-illustration-public-title">{art.caption}</span> : null}
                      {art.credit ? <span className="ns-scene-illustration-public-credit">{ui.illustrationCredit}: {art.credit}</span> : null}
                    </figcaption>
                  ) : null}
                </figure>
              ) : null}

              <details className="ns-collapsible-note ns-illustration-modal-details">
                <summary>{ui.illustrationRulesSummary}</summary>
                <p>{ui.illustrationApprovalGuardrail}</p>
              </details>

              <div className="ns-illustration-modal-actions">
                <button className="ns-button ns-button-outline" type="button" onClick={closeApproveIllustrationModal} disabled={Boolean(workingAction)}>
                  {ui.cancel}
                </button>
                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  onClick={confirmApproveSceneIllustration}
                  disabled={Boolean(workingAction)}
                >
                  {workingAction === `approve-illustration-${sceneId}` ? ui.working : ui.illustrationApproveNow}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {rejectIllustrationModal.open && rejectIllustrationModal.scene && (
        <div
          role="dialog"
          aria-modal="true"
          className="ns-modal-backdrop"
          onClick={closeRejectIllustrationModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1260,
          }}
        >
          <div
            className="ns-card ns-card-soft"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(680px, 100%)", padding: 18, borderRadius: 18, background: "#ffffff" }}
          >
            <h2 className="ns-section-title" style={{ marginTop: 0, marginBottom: 8 }}>
              {ui.illustrationRejectTitle}
            </h2>
            <p className="ns-card-meta" style={{ marginBottom: 12 }}>{ui.illustrationRejectHelp}</p>

            <div style={{ marginBottom: 12 }}>
              <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                <strong>{safeSceneTitle(rejectIllustrationModal.scene, ui)}</strong>
              </p>
              <div style={{ lineHeight: "1.55", color: "#334155" }}>
                {clampWords(safeSceneText(rejectIllustrationModal.scene), 32) || ui.unknown}
              </div>
            </div>

            <label style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              <span className="ns-card-meta"><strong>{ui.illustrationRejectReasonLabel}</strong></span>
              <select
                className="ns-input"
                value={rejectIllustrationModal.reason}
                onChange={(e) => setRejectIllustrationModal((current) => ({ ...current, reason: e.target.value }))}
                style={{ minHeight: 42 }}
              >
                {illustrationRejectionReasonOptions(ui).map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span className="ns-card-meta"><strong>{ui.illustrationRejectNoteLabel}</strong></span>
              <textarea
                value={rejectIllustrationModal.note}
                onChange={(e) => setRejectIllustrationModal((current) => ({ ...current, note: e.target.value }))}
                placeholder={ui.illustrationRejectNotePlaceholder}
                style={{
                  width: "100%",
                  minHeight: 110,
                  resize: "vertical",
                  borderRadius: 14,
                  border: "1px solid rgba(15,23,42,0.16)",
                  padding: 12,
                  fontSize: 15,
                  lineHeight: 1.55,
                  boxSizing: "border-box",
                }}
              />
            </label>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button className="ns-button ns-button-outline" type="button" onClick={closeRejectIllustrationModal} disabled={Boolean(workingAction)}>
                {ui.cancel}
              </button>
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={confirmRejectSceneIllustration}
                disabled={Boolean(workingAction) || !safeText(rejectIllustrationModal.reason)}
              >
                {workingAction ? ui.working : ui.illustrationRejectNow}
              </button>
            </div>
          </div>
        </div>
      )}

      {changeIllustrationModal.open && changeIllustrationModal.scene && (() => {
        const sceneId = safeText(changeIllustrationModal.scene?._id || changeIllustrationModal.scene?.id);
        const existingArt = safeSceneIllustration(changeIllustrationModal.scene);
        const busy = workingAction === `replace-illustration-${sceneId}` || changeIllustrationModal.uploading;
        const modalTitle = existingArt ? ui.changeIllustrationTitle : ui.addIllustrationTitle;
        const modalHelp = existingArt ? ui.changeIllustrationHelp : ui.addIllustrationHelp;
        const modalConfirm = existingArt ? ui.changeIllustrationConfirm : ui.addIllustrationConfirm;
        return (
        <div
          className="ns-modal-backdrop ns-change-illustration-backdrop"
          role="presentation"
          onClick={closeChangeIllustrationModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
            background: "rgba(15, 23, 42, 0.58)",
          }}
        >
          <div
            className="ns-modal ns-dashboard-modal"
            role="dialog"
            aria-modal="true"
            aria-label={modalTitle}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(760px, 100%)",
              maxHeight: "88vh",
              overflowY: "auto",
              borderRadius: 22,
              background: "#ffffff",
              padding: 18,
              boxShadow: "0 28px 80px rgba(15, 23, 42, 0.36)",
            }}
          >
            <div className="ns-modal-header">
              <div>
                <div className="ns-modal-kicker">{ui.sceneIllustration}</div>
                <h2>{modalTitle}</h2>
              </div>
            </div>

            <p className="ns-card-meta" style={{ marginTop: 8 }}>{modalHelp}</p>

            <div className="ns-card-soft" style={{ marginTop: 12, padding: 12, borderRadius: 14 }}>
              <strong>{safeSceneTitle(changeIllustrationModal.scene, ui)}</strong>
              <p className="ns-card-meta" style={{ marginTop: 4 }}>
                {clampWords(safeSceneText(changeIllustrationModal.scene), 28) || ui.unknown}
              </p>
            </div>

            <div className="ns-illustration-change-form" style={{ marginTop: 14 }}>
              <label className="ns-field">
                <span>{ui.changeIllustrationFileLabel}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setChangeIllustrationModal((current) => ({ ...current, file }));
                  }}
                />
              </label>

              <label className="ns-field">
                <span>{ui.changeIllustrationUrlLabel}</span>
                <input
                  type="url"
                  value={changeIllustrationModal.url}
                  disabled={busy}
                  onChange={(e) => setChangeIllustrationModal((current) => ({ ...current, url: e.target.value }))}
                  placeholder="https://..."
                />
              </label>

              <label className="ns-field">
                <span>{ui.changeIllustrationCaptionLabel}</span>
                <input
                  type="text"
                  value={changeIllustrationModal.caption}
                  disabled={busy}
                  onChange={(e) => setChangeIllustrationModal((current) => ({ ...current, caption: e.target.value }))}
                />
              </label>

              <label className="ns-field">
                <span>{ui.changeIllustrationCreditLabel}</span>
                <input
                  type="text"
                  value={changeIllustrationModal.credit}
                  disabled={busy}
                  onChange={(e) => setChangeIllustrationModal((current) => ({ ...current, credit: e.target.value }))}
                />
              </label>

              <label className="ns-field">
                <span>{ui.changeIllustrationAltLabel}</span>
                <input
                  type="text"
                  value={changeIllustrationModal.alt}
                  disabled={busy}
                  onChange={(e) => setChangeIllustrationModal((current) => ({ ...current, alt: e.target.value }))}
                />
              </label>
            </div>

            <div className="ns-modal-actions" style={{ marginTop: 16 }}>
              <button className="ns-button ns-button-outline" type="button" onClick={closeChangeIllustrationModal} disabled={busy}>
                {ui.cancel}
              </button>
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={confirmChangeSceneIllustration}
                disabled={busy || (!changeIllustrationModal.file && !safeText(changeIllustrationModal.url))}
              >
                {busy ? ui.imageUploading : modalConfirm}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {sceneModal.open && sceneModal.scene && (
        <div
          role="dialog"
          aria-modal="true"
          className="ns-modal-backdrop"
          onClick={closeSceneModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1200,
          }}
        >
          <div
            className="ns-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(920px, 100%)",
              maxHeight: "88vh",
              overflowY: "auto",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <div className="ns-card-title" style={{ marginBottom: 0 }}>
                {sceneModal.kind === "canon" && sceneModal.number
                  ? `${ui.scene} ${sceneModal.number}`
                  : ui.proposal}
              </div>
              <Pill
                {...sceneStatusPillMeta(sceneModal.kind === "canon" ? "canon" : "pending", ui)}
                style={{ fontSize: 12, padding: "4px 8px" }}
              />
            </div>

            <p className="ns-card-meta" style={{ marginBottom: 8 }}>
              <strong>{safeSceneTitle(sceneModal.scene, ui)}</strong>
            </p>

            <p className="ns-card-meta" style={{ marginBottom: 12 }}>
              {formatDateTime(sceneModal.scene?.createdAt || sceneModal.scene?.updatedAt, lang)}
            </p>

            {sceneModal.kind === "canon" ? (
              <div className="ns-dashboard-scene-signal-row">
                <SceneRatingSummaryPill
                  summary={sceneRatingSummaries[sceneStableId(sceneModal.scene)] || null}
                  loading={sceneRatingsLoading}
                  ui={ui}
                />
                <SceneCompletionSummaryPill
                  summary={sceneCompletionSummaries[sceneStableId(sceneModal.scene)] || null}
                  loading={sceneCompletionLoading}
                  ui={ui}
                />
                <SceneReactionSummaryPill
                  summary={sceneReactionSummaries[sceneStableId(sceneModal.scene)] || null}
                  loading={sceneReactionsLoading}
                  ui={ui}
                />
              </div>
            ) : null}

            <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.7", color: "#0f172a", marginBottom: 14 }}>
              {safeSceneText(sceneModal.scene) || ui.unknown}
            </div>

            <SceneIllustrationDashboard
              scene={sceneModal.scene}
              ui={ui}
              onApprove={approveSceneIllustration}
              onReject={rejectSceneIllustration}
              onChangeImage={openChangeIllustrationModal}
              workingAction={workingAction}
            />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              {sceneModal.kind === "canon" && (
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() => removeCanonScene(sceneModal.scene)}
                  disabled={workingAction === `remove-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}`}
                  style={{ minHeight: 40 }}
                >
                  {workingAction === `remove-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}` ? ui.working : ui.removeScene}
                </button>
              )}

              {sceneModal.kind === "proposal" && (
                <>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => approveProposal(sceneModal.scene)}
                    disabled={workingAction === `approve-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}`}
                    style={{ minHeight: 40 }}
                  >
                    {workingAction === `approve-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}` ? ui.working : ui.approve}
                  </button>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => rejectProposal(sceneModal.scene)}
                    disabled={workingAction === `reject-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}`}
                    style={{ minHeight: 40 }}
                  >
                    {workingAction === `reject-${safeText(sceneModal.scene?._id || sceneModal.scene?.id)}` ? ui.working : ui.reject}
                  </button>
                </>
              )}

              <button className="ns-button ns-button-outline" type="button" onClick={closeSceneModal} style={{ minHeight: 40 }}>
                {ui.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <StoryDashboardOwnerSettings
        open={ownerSettingsOpen}
        story={selectedStory}
        storyId={selectedId}
        lang={lang}
        onClose={() => setOwnerSettingsOpen(false)}
        onSaved={async () => {
          await reloadSelectedStory();
        }}
      />

      {editOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="ns-modal-backdrop"
          onClick={closeEditModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1200,
          }}
        >
          <div
            className="ns-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(860px, 100%)",
              maxHeight: "88vh",
              overflowY: "auto",
              padding: 16,
            }}
          >
            <div className="ns-card-title" style={{ marginBottom: 12 }}>
              {ui.metadataEditor}
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span className="ns-card-meta"><strong>{ui.titleLabel}</strong></span>
                <input
                  className="ns-input"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="ns-card-meta"><strong>{ui.description}</strong></span>
                <textarea
                  className="ns-input"
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={5}
                  style={{ resize: "vertical" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="ns-card-meta"><strong>{ui.synopsis}</strong></span>
                <textarea
                  className="ns-input"
                  value={editForm.synopsis}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, synopsis: e.target.value }))}
                  rows={4}
                  style={{ resize: "vertical" }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="ns-card-meta"><strong>{ui.aboutAuthor}</strong></span>
                <textarea
                  className="ns-input"
                  value={editForm.aboutAuthor}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, aboutAuthor: e.target.value }))}
                  rows={4}
                  style={{ resize: "vertical" }}
                />
              </label>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              <button className="ns-button ns-button-outline" type="button" onClick={saveStoryDetails} disabled={workingAction === "save-details"} style={{ minHeight: 40 }}>
                {workingAction === "save-details" ? ui.saving : ui.saveDetails}
              </button>
              <button className="ns-button ns-button-outline" type="button" onClick={closeEditModal} style={{ minHeight: 40 }}>
                {ui.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
